import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';
import mockEscala from './mocks/mock.json';
import { MdKeyboardArrowDown, MdKeyboardArrowLeft } from 'react-icons/md';
import {
	FaAmbulance,
	FaBone,
	FaBrain,
	FaChild,
	FaClinicMedical,
	FaDiagnoses,
	FaDna,
	FaEye,
	FaHeartbeat,
	FaHeadSideVirus,
	FaHospitalUser,
	FaLungs,
	FaMicroscope,
	FaPills,
	FaProcedures,
	FaRegFilePdf,
	FaStethoscope,
	FaSyringe,
	FaTooth,
	FaUserMd,
	FaXRay,
} from 'react-icons/fa';
import { exportarPdfGridEscala } from './PDFgrid';

const DIAS_ORDEM = [
	'Segunda-feira',
	'Terça-feira',
	'Quarta-feira',
	'Quinta-feira',
	'Sexta-feira',
	'Sábado',
	'Domingo',
];

const STATUS_CELL_CLASS = {
	SUGERIDO: 'bg-[#4D6C92] border border-[#4D6C92]',
	ATRIBUIDO: 'bg-[#4D6C92] border border-[#4D6C92]',
	VACANCIA: 'bg-[#BD504E] border border-[#BD504E]',
	FERIAS: 'bg-[#6ACEF8] border border-[#6ACEF8]',
	ATESTADO: 'bg-[#FB965C] border border-[#FB965C]',
	FOLGA: 'bg-[#8BBD8B] border border-[#8BBD8B]',
	LICENCA: 'bg-[#A476C7] border border-[#A476C7]',
	FALTA: 'bg-[#545454] border border-[#545454]',
	TROCA: 'bg-[#967771] border border-[#967771]',
	DATA_ESPECIFICA: 'bg-[#e685db] border border-[#e685db]',
	BLOQUEADO: 'bg-gray-300 border border-gray-300',
	NAO_ATRIBUIDO: '',
};

const STATUS_TEXT_CLASS = {
	SUGERIDO: 'text-[#4D6C92]',
	ATRIBUIDO: 'text-[#4D6C92]',
	VACANCIA: 'text-[#BD504E]',
	FERIAS: 'text-[#6ACEF8]',
	ATESTADO: 'text-[#FB965C]',
	FOLGA: 'text-[#8BBD8B]',
	LICENCA: 'text-[#A476C7]',
	FALTA: 'text-[#545454]',
	TROCA: 'text-[#967771]',
	DATA_ESPECIFICA: 'text-[#e685db]',
	BLOQUEADO: 'text-gray-300',
	NAO_ATRIBUIDO: 'text-gray-300',
};

const normalizarDiaSemana = (dia = '') => {
	const base = String(dia)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

	if (base.includes('segunda')) return 'Segunda-feira';
	if (base.includes('terca')) return 'Terça-feira';
	if (base.includes('quarta')) return 'Quarta-feira';
	if (base.includes('quinta')) return 'Quinta-feira';
	if (base.includes('sexta')) return 'Sexta-feira';
	if (base.includes('sabado')) return 'Sábado';
	if (base.includes('domingo')) return 'Domingo';

	return null;
};

const parseISODateAsLocal = (valorData) => {
	if (!valorData) return null;

	const texto = String(valorData).trim();
	const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);

	if (!match) {
		const dataGenerica = new Date(texto);
		return Number.isNaN(dataGenerica.getTime()) ? null : dataGenerica;
	}

	const ano = Number(match[1]);
	const mes = Number(match[2]);
	const dia = Number(match[3]);
	const dataLocal = new Date(ano, mes - 1, dia, 0, 0, 0, 0);

	if (Number.isNaN(dataLocal.getTime())) return null;
	return dataLocal;
};

const getDiaSemanaNumero = (valorData) => {
	const data = parseISODateAsLocal(valorData);
	if (!data) return null;

	const diaJS = data.getDay();
	return diaJS === 0 ? 7 : diaJS;
};

const formatarDataPtBr = (valorData) => {
	if (!valorData) return '';
	const data = parseISODateAsLocal(valorData);
	if (!data) return String(valorData);
	if (Number.isNaN(data.getTime())) return String(valorData);
	return data.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
};

const getHojeBrasiliaISO = () => {
	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'America/Sao_Paulo',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});

	const parts = formatter.formatToParts(new Date());
	const year = parts.find((part) => part.type === 'year')?.value;
	const month = parts.find((part) => part.type === 'month')?.value;
	const day = parts.find((part) => part.type === 'day')?.value;

	if (!year || !month || !day) return '';
	return `${year}-${month}-${day}`;
};

const buildSemanasDoMes = (colunas = []) => {
	if (!Array.isArray(colunas) || colunas.length === 0) return [];

	const colunasOrdenadas = [...colunas].sort((a, b) => {
		const dataA = String(a?.data || '');
		const dataB = String(b?.data || '');
		return dataA.localeCompare(dataB);
	});

	const semanas = [];
	let semanaAtual = [];

	colunasOrdenadas.forEach((coluna) => {
		semanaAtual.push(coluna);

		const diaSemana =
			getDiaSemanaNumero(coluna?.data) ?? Number(coluna?.dia_semana);

		if (diaSemana === 7) {
			semanas.push(semanaAtual);
			semanaAtual = [];
		}
	});

	if (semanaAtual.length) semanas.push(semanaAtual);

	return semanas;
};

const extrairDataReal = (item, celula) =>
	item?.data ||
	item?.data_referencia ||
	item?.data_item ||
	item?.data_inicio ||
	celula?.data ||
	celula?.data_referencia ||
	celula?.data_item ||
	'';

// Omite o último sobrenome quando o nome completo não cabe no card.
// Mantém pelo menos 2 palavras. Retorna string com '...' quando truncado.
const smartTruncateName = (fullName = '') => {
	const name = String(fullName).trim();
	const words = name.split(/\s+/).filter(Boolean);
	if (words.length <= 2) return name;

	const MAX_CHARS = 20;
	if (name.length <= MAX_CHARS) return name;

	for (let count = words.length - 1; count >= 2; count--) {
		const candidate = words.slice(0, count).join(' ');
		if (candidate.length <= MAX_CHARS) {
			return candidate;
		}
	}

	return words.slice(0, 2).join(' ');
};

const compactarTituloProfissionalNoCard = (fullName = '') => {
	const name = String(fullName).trim();
	const words = name.split(/\s+/).filter(Boolean);

	if (words.length < 2 || name.length <= 16) return name;

	return `${words[0]} ${words[1].charAt(0)}.`;
};

const normalizarEspecialidade = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const extrairRotulo = (valor) => {
	if (valor === null || valor === undefined) return '';
	if (typeof valor === 'string' || typeof valor === 'number') {
		return String(valor).trim();
	}
	if (typeof valor !== 'object') return '';

	const candidato =
		valor?.nome ||
		valor?.descricao ||
		valor?.label ||
		valor?.titulo ||
		valor?.codigo;

	if (candidato === valor) return '';
	return extrairRotulo(candidato);
};

const primeiroRotulo = (...valores) => {
	for (const valor of valores) {
		const rotulo = extrairRotulo(valor);
		if (rotulo) return rotulo;
	}
	return '';
};

const getEspecialidadeItem = (item, linha = null) =>
	primeiroRotulo(
		item?.especialidade,
		item?.especialidade_nome,
		item?.nome_especialidade,
		item?.resumo?.especialidade,
		item?.edicao?.profissional_por_cpf?.especialidade_nome,
		item?.dados_edicao?.especialidade,
		item?.dados_edicao?.nome_especialidade,
		linha?.especialidade,
		linha?.vinculo_profissional?.especialidade,
	);

const getVinculoItem = (item, linha = null) =>
	primeiroRotulo(
		item?.vinculo,
		item?.nome_vinculo,
		item?.vinculo_nome,
		item?.descricao_vinculo,
		item?.resumo?.vinculo,
		item?.edicao?.profissional_por_cpf?.vinculo_tipo_nome,
		item?.dados_edicao?.vinculo,
		item?.dados_edicao?.nome_vinculo,
		linha?.vinculo_profissional?.nome_vinculo,
		linha?.vinculo_profissional?.vinculo_tipo_nome,
		linha?.vinculo_profissional?.tipo_vinculo,
		linha?.vinculo_profissional?.vinculo,
		linha?.vinculo,
	);

const ESPECIALIDADE_ICONES = [
	{
		matches: ['neuro'],
		especialidadeFundo: '#EAF5FF',
		especialidadeTextoCor: '#1E8BFF',
		IconeEspecialidade: FaBrain,
	},
	{
		matches: ['clinica', 'medica', 'geral'],
		especialidadeFundo: '#EDFBEA',
		especialidadeTextoCor: '#31A94D',
		IconeEspecialidade: FaStethoscope,
	},
	{
		matches: ['cardio'],
		especialidadeFundo: '#F5EAFD',
		especialidadeTextoCor: '#A85AF5',
		IconeEspecialidade: FaHeartbeat,
	},
	{
		matches: ['ortopedia', 'ortoped', 'traumato'],
		especialidadeFundo: '#FFF2E1',
		especialidadeTextoCor: '#FF8A1C',
		IconeEspecialidade: FaBone,
	},
	{
		matches: ['pediatria', 'pediatr'],
		especialidadeFundo: '#E8FBFB',
		especialidadeTextoCor: '#18BFC6',
		IconeEspecialidade: FaChild,
	},
	{
		matches: ['intensiva', 'uti', 'terapia intensiva'],
		IconeEspecialidade: FaProcedures,
	},
	{
		matches: ['radio', 'imagem', 'diagnostico por imagem'],
		IconeEspecialidade: FaXRay,
	},
];

const ESPECIALIDADE_ICONES_FALLBACK = [
	FaUserMd,
	FaClinicMedical,
	FaDiagnoses,
	FaHospitalUser,
	FaMicroscope,
	FaDna,
	FaPills,
	FaSyringe,
	FaLungs,
	FaEye,
	FaHeadSideVirus,
	FaAmbulance,
	FaTooth,
];

// Paleta central das especialidades.
// Para testar outra cor, altere a mesma entrada nos campos abaixo.
// especialidadeTextoCor = cor base usada em texto, tarja lateral e bolinhas.
// especialidadeFundo = referencia suave da mesma familia.
const ESPECIALIDADE_PALETA = [
	{
		especialidadeFundo: '#E8F3F8',
		especialidadeTextoCor: '#4F86A8',
		dot: 'bg-[#4F86A8]',
		text: 'text-[#4F86A8]',
		border: 'border-[#4F86A8]/25',
		background: 'bg-[#4F86A8]/10',
		legendDot: 'bg-[#4F86A8]/35',
	},
	{
		especialidadeFundo: '#E9F5F2',
		especialidadeTextoCor: '#5E9C8D',
		dot: 'bg-[#5E9C8D]',
		text: 'text-[#5E9C8D]',
		border: 'border-[#5E9C8D]/25',
		background: 'bg-[#5E9C8D]/10',
		legendDot: 'bg-[#5E9C8D]/35',
	},
	{
		especialidadeFundo: '#F0EAF6',
		especialidadeTextoCor: '#9A7BB6',
		dot: 'bg-[#9A7BB6]',
		text: 'text-[#9A7BB6]',
		border: 'border-[#9A7BB6]/25',
		background: 'bg-[#9A7BB6]/10',
		legendDot: 'bg-[#9A7BB6]/35',
	},
	{
		especialidadeFundo: '#F7E9EF',
		especialidadeTextoCor: '#C97D97',
		dot: 'bg-[#C97D97]',
		text: 'text-[#C97D97]',
		border: 'border-[#C97D97]/25',
		background: 'bg-[#C97D97]/10',
		legendDot: 'bg-[#C97D97]/35',
	},
	{
		especialidadeFundo: '#F6F0D8',
		especialidadeTextoCor: '#B8A45C',
		dot: 'bg-[#B8A45C]',
		text: 'text-[#B8A45C]',
		border: 'border-[#B8A45C]/25',
		background: 'bg-[#B8A45C]/10',
		legendDot: 'bg-[#B8A45C]/35',
	},
	{
		especialidadeFundo: '#F6E8E1',
		especialidadeTextoCor: '#C58A72',
		dot: 'bg-[#C58A72]',
		text: 'text-[#C58A72]',
		border: 'border-[#C58A72]/25',
		background: 'bg-[#C58A72]/10',
		legendDot: 'bg-[#C58A72]/35',
	},
	{
		especialidadeFundo: '#E7F2F6',
		especialidadeTextoCor: '#6E9DB5',
		dot: 'bg-[#6E9DB5]',
		text: 'text-[#6E9DB5]',
		border: 'border-[#6E9DB5]/25',
		background: 'bg-[#6E9DB5]/10',
		legendDot: 'bg-[#6E9DB5]/35',
	},
	{
		especialidadeFundo: '#EAF5ED',
		especialidadeTextoCor: '#77A985',
		dot: 'bg-[#77A985]',
		text: 'text-[#77A985]',
		border: 'border-[#77A985]/25',
		background: 'bg-[#77A985]/10',
		legendDot: 'bg-[#77A985]/35',
	},
	{
		especialidadeFundo: '#ECEEFA',
		especialidadeTextoCor: '#8B8FC6',
		dot: 'bg-[#8B8FC6]',
		text: 'text-[#8B8FC6]',
		border: 'border-[#8B8FC6]/25',
		background: 'bg-[#8B8FC6]/10',
		legendDot: 'bg-[#8B8FC6]/35',
	},
	{
		especialidadeFundo: '#F4E8F0',
		especialidadeTextoCor: '#B77FA8',
		dot: 'bg-[#B77FA8]',
		text: 'text-[#B77FA8]',
		border: 'border-[#B77FA8]/25',
		background: 'bg-[#B77FA8]/10',
		legendDot: 'bg-[#B77FA8]/35',
	},
	{
		especialidadeFundo: '#F4EDDF',
		especialidadeTextoCor: '#B99B6A',
		dot: 'bg-[#B99B6A]',
		text: 'text-[#B99B6A]',
		border: 'border-[#B99B6A]/25',
		background: 'bg-[#B99B6A]/10',
		legendDot: 'bg-[#B99B6A]/35',
	},
	{
		especialidadeFundo: '#F8EAE4',
		especialidadeTextoCor: '#D09882',
		dot: 'bg-[#D09882]',
		text: 'text-[#D09882]',
		border: 'border-[#D09882]/25',
		background: 'bg-[#D09882]/10',
		legendDot: 'bg-[#D09882]/35',
	},
	{
		especialidadeFundo: '#E8F4F4',
		especialidadeTextoCor: '#6FA6A9',
		dot: 'bg-[#6FA6A9]',
		text: 'text-[#6FA6A9]',
		border: 'border-[#6FA6A9]/25',
		background: 'bg-[#6FA6A9]/10',
		legendDot: 'bg-[#6FA6A9]/35',
	},
	{
		especialidadeFundo: '#F0EAF6',
		especialidadeTextoCor: '#A58AC0',
		dot: 'bg-[#A58AC0]',
		text: 'text-[#A58AC0]',
		border: 'border-[#A58AC0]/25',
		background: 'bg-[#A58AC0]/10',
		legendDot: 'bg-[#A58AC0]/35',
	},
	{
		especialidadeFundo: '#E9F0F6',
		especialidadeTextoCor: '#7E9BB7',
		dot: 'bg-[#7E9BB7]',
		text: 'text-[#7E9BB7]',
		border: 'border-[#7E9BB7]/25',
		background: 'bg-[#7E9BB7]/10',
		legendDot: 'bg-[#7E9BB7]/35',
	},
	{
		especialidadeFundo: '#EEF3E3',
		especialidadeTextoCor: '#9AAE72',
		dot: 'bg-[#9AAE72]',
		text: 'text-[#9AAE72]',
		border: 'border-[#9AAE72]/25',
		background: 'bg-[#9AAE72]/10',
		legendDot: 'bg-[#9AAE72]/35',
	},
];

const VINCULO_PALETA = [
	{
		dot: 'bg-[#0F766E]',
		text: 'text-[#0F766E]',
		border: 'border-[#0F766E]/25',
		background: 'bg-[#0F766E]/10',
	},
	{
		dot: 'bg-[#7C3AED]',
		text: 'text-[#7C3AED]',
		border: 'border-[#7C3AED]/25',
		background: 'bg-[#7C3AED]/10',
	},
	{
		dot: 'bg-[#BE123C]',
		text: 'text-[#BE123C]',
		border: 'border-[#BE123C]/25',
		background: 'bg-[#BE123C]/10',
	},
	{
		dot: 'bg-[#0369A1]',
		text: 'text-[#0369A1]',
		border: 'border-[#0369A1]/25',
		background: 'bg-[#0369A1]/10',
	},
	{
		dot: 'bg-[#B45309]',
		text: 'text-[#B45309]',
		border: 'border-[#B45309]/25',
		background: 'bg-[#B45309]/10',
	},
	{
		dot: 'bg-[#15803D]',
		text: 'text-[#15803D]',
		border: 'border-[#15803D]/25',
		background: 'bg-[#15803D]/10',
	},
	{
		dot: 'bg-[#4338CA]',
		text: 'text-[#4338CA]',
		border: 'border-[#4338CA]/25',
		background: 'bg-[#4338CA]/10',
	},
	{
		dot: 'bg-[#A21CAF]',
		text: 'text-[#A21CAF]',
		border: 'border-[#A21CAF]/25',
		background: 'bg-[#A21CAF]/10',
	},
];

const getPaletteIndexByText = (value = '', paletteLength = 1) => {
	const normalized = normalizarEspecialidade(value || 'sem valor');
	let hash = 0;

	for (let index = 0; index < normalized.length; index += 1) {
		hash = (hash * 31 + normalized.charCodeAt(index)) >>> 0;
	}

	return hash % Math.max(1, paletteLength);
};

const getVinculoClasses = (value = '') =>
	VINCULO_PALETA[
		getPaletteIndexByText(value || 'Sem vínculo', VINCULO_PALETA.length)
	] || VINCULO_PALETA[0];

const getEspecialidadeCor = (paletteIndex = 0) => {
	if (paletteIndex < ESPECIALIDADE_PALETA.length) {
		const { especialidadeFundo, especialidadeTextoCor } =
			ESPECIALIDADE_PALETA[paletteIndex];
		return { especialidadeFundo, especialidadeTextoCor };
	}

	const hue = Math.round(
		((paletteIndex - ESPECIALIDADE_PALETA.length) * 47) % 360,
	);
	return {
		especialidadeFundo: `hsl(${hue}, 58%, 84%)`,
		especialidadeTextoCor: `hsl(${hue}, 48%, 38%)`,
	};
};

const getEspecialidadeClasses = (paletteIndex = 0) =>
	ESPECIALIDADE_PALETA[paletteIndex % ESPECIALIDADE_PALETA.length] ||
	ESPECIALIDADE_PALETA[0];

const getSpecialtyVisual = (
	value = '',
	paletteIndex = 0,
	usedIcons = null,
	paletteOffset = 0,
) => {
	const text = normalizarEspecialidade(value || 'Não informada');
	const preset = ESPECIALIDADE_ICONES.find((item) =>
		item.matches.some((match) => text.includes(match)),
	);

	const visualPaletteIndex = paletteIndex + paletteOffset;
	const cor = getEspecialidadeCor(visualPaletteIndex);
	const iconOptions = [
		preset?.IconeEspecialidade,
		...ESPECIALIDADE_ICONES_FALLBACK,
	].filter(Boolean);
	let IconeEspecialidade =
		iconOptions[paletteIndex % iconOptions.length] || FaStethoscope;

	if (usedIcons) {
		IconeEspecialidade =
			iconOptions.find((Icone) => !usedIcons.has(Icone)) ||
			IconeEspecialidade;
		usedIcons.add(IconeEspecialidade);
	}

	return {
		especialidadeFundo: cor.especialidadeFundo,
		especialidadeTextoCor: cor.especialidadeTextoCor,
		especialidadeClasses: getEspecialidadeClasses(visualPaletteIndex),
		IconeEspecialidade,
	};
};

function ApiScaleGrid({
	payload,
	onEditItem,
	onDeleteItem,
	canManageItems = true,
	canModifyItems = true,
	tituloEscala = null,
	agruparPorEspecialidade = false,
	regime = '',
	modoMensal = false,
	rotuloAgrupamento = 'Área de atuação',
	exportarPdfCustom = null,
	seguirTituloAgrupamentoNoScroll = false,
	abreviarTurnoCompacto = false,
	compactarNomeProfissionalNoCard = false,
	preservarAlturaNomeSetor = false,
}) {
	const data = payload || mockEscala;
	const visualizacaoPayload = useMemo(
		() => data?.visualizacao || data?.grade || {},
		[data],
	);
	const [itemIdParaExcluir, setItemIdParaExcluir] = useState(null);
	const [isDeletingItem, setIsDeletingItem] = useState(false);
	const [isExportingPdf, setIsExportingPdf] = useState(false);
	const [exportPdfError, setExportPdfError] = useState('');
	const [mostrarLegendaCards, setMostrarLegendaCards] = useState(false);
	const [especialidadeTurnosAberta, setEspecialidadeTurnosAberta] =
		useState(null);
	const [menuTurnosFlutuante, setMenuTurnosFlutuante] = useState(null);
	const [agrupamentoAtivo, setAgrupamentoAtivo] = useState('especialidade');
	const [legendaAgrupamentoAberta, setLegendaAgrupamentoAberta] =
		useState(null);
	const [agrupamentosColapsados, setAgrupamentosColapsados] = useState({});
	const [ordenacaoAtiva, setOrdenacaoAtiva] = useState('horario');
	const [turnosCompactos, setTurnosCompactos] = useState({});
	const [semanaSelecionadaIndex, setSemanaSelecionadaIndex] = useState(0);
	const [alturaNomeSetorRenderizado, setAlturaNomeSetorRenderizado] =
		useState(0);
	const initialWeekSelected = useRef(false);
	const linhasRefs = useRef({});
	const gradeScrollRef = useRef(null);
	const floatingLabelRefs = useRef(new Set());
	const tituloAgrupamentoRefs = useRef(new Set());

	const registerFloatingLabel = useCallback(
		(element) => {
			if (element) {
				floatingLabelRefs.current.add(element);
			}
		},
		[],
	);

	const registerTituloAgrupamento = useCallback((element) => {
		if (element) tituloAgrupamentoRefs.current.add(element);
	}, []);

	const atualizarTitulosAgrupamento = useCallback(() => {
		if (!seguirTituloAgrupamentoNoScroll) return;
		const deslocamento = gradeScrollRef.current?.scrollLeft || 0;

		tituloAgrupamentoRefs.current.forEach((titulo) => {
			if (!titulo?.isConnected) {
				tituloAgrupamentoRefs.current.delete(titulo);
				return;
			}
			titulo.style.transform = `translateX(${deslocamento + 42}px)`;
		});
	}, [seguirTituloAgrupamentoNoScroll]);

	useEffect(() => {
		atualizarTitulosAgrupamento();
		window.addEventListener('resize', atualizarTitulosAgrupamento);
		return () => window.removeEventListener('resize', atualizarTitulosAgrupamento);
	}, [atualizarTitulosAgrupamento]);

	const fecharPopupExclusao = () => {
		if (isDeletingItem) return;
		setItemIdParaExcluir(null);
	};

	const confirmarExclusaoItem = async () => {
		if (!canManageItems || !canModifyItems) return;
		if (!itemIdParaExcluir || isDeletingItem) return;
		setIsDeletingItem(true);
		try {
			await onDeleteItem?.(itemIdParaExcluir);
			setItemIdParaExcluir(null);
		} finally {
			setIsDeletingItem(false);
		}
	};

	const normalizarTexto = useCallback(
		(texto = '') =>
			String(texto)
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim(),
		[],
	);

	const regimeNormalizado = normalizarTexto(
		regime ||
			data?.contexto?.regime ||
			data?.filtros?.regime ||
			visualizacaoPayload?.regime ||
			'',
	);

	const deveGarantirTurnosBasicos =
		preservarAlturaNomeSetor ||
		regimeNormalizado === 'plantonista' ||
		regimeNormalizado === 'rotina' ||
		regimeNormalizado === 'todos' ||
		regimeNormalizado === 'todo' ||
		regimeNormalizado === 'all';

	const rolarParaLinha = (linhaKey) => {
		const linhaElement = linhasRefs.current[linhaKey];
		if (!linhaElement) return;

		linhaElement.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		setEspecialidadeTurnosAberta(null);
		setMenuTurnosFlutuante(null);
	};

	const selecionarAgrupamento = (tipo) => {
		setAgrupamentoAtivo(tipo);
		setEspecialidadeTurnosAberta(null);
		setMenuTurnosFlutuante(null);
		setLegendaAgrupamentoAberta((atual) => (atual === tipo ? null : tipo));
	};

	const alternarAgrupamentoColapsado = (key) => {
		setAgrupamentosColapsados((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	useEffect(() => {
		if (typeof window === 'undefined') return undefined;

		let animationFrameId = null;

		const updateFloatingLabels = () => {
			animationFrameId = null;
			const viewportCenter = window.innerHeight / 2;
			const labelPadding = 88;
			const proximosTurnosCompactos = {};

			floatingLabelRefs.current.forEach((label) => {
				if (!label?.isConnected) {
					floatingLabelRefs.current.delete(label);
					return;
				}

				const container = label.parentElement;
				if (!container) return;

				const containerRect = container.getBoundingClientRect();
				const containerHeight = container.offsetHeight;
				const labelHeight = label.offsetHeight;

				if (!containerHeight || !labelHeight) return;
				const turnoKey = label.dataset.turnoKey;
				const turnoNome = label.dataset.turnoNome || '';
				if (abreviarTurnoCompacto && turnoKey && turnoNome) {
					const alturaNecessaria = Math.max(
						44,
						turnoNome.length * 11 + 28,
					);
					proximosTurnosCompactos[turnoKey] =
						containerHeight < alturaNecessaria;
				}

				const centeredY =
					viewportCenter - containerRect.top - labelHeight / 2;
				const maxY = Math.max(
					labelPadding,
					containerHeight - labelHeight - labelPadding,
				);
				const y =
					containerHeight <= labelHeight + labelPadding * 2
						? Math.max(0, (containerHeight - labelHeight) / 2)
						: Math.min(Math.max(centeredY, labelPadding), maxY);

				label.style.setProperty('--floating-label-y', `${y}px`);
			});

			if (abreviarTurnoCompacto) {
				setTurnosCompactos((anteriores) => {
					const chaves = new Set([
						...Object.keys(anteriores),
						...Object.keys(proximosTurnosCompactos),
					]);
					const alterou = Array.from(chaves).some(
						(chave) =>
							Boolean(anteriores[chave]) !==
							Boolean(proximosTurnosCompactos[chave]),
					);
					return alterou ? proximosTurnosCompactos : anteriores;
				});
			}
		};

		const scheduleFloatingLabelsUpdate = () => {
			if (animationFrameId !== null) return;
			animationFrameId = window.requestAnimationFrame(
				updateFloatingLabels,
			);
		};

		scheduleFloatingLabelsUpdate();
		window.addEventListener('scroll', scheduleFloatingLabelsUpdate, {
			passive: true,
		});
		window.addEventListener('resize', scheduleFloatingLabelsUpdate);

		return () => {
			if (animationFrameId !== null) {
				window.cancelAnimationFrame(animationFrameId);
			}
			window.removeEventListener('scroll', scheduleFloatingLabelsUpdate);
			window.removeEventListener('resize', scheduleFloatingLabelsUpdate);
		};
	});

	const alternarMenuTurnos = (item, elementoAncora) => {
		if (especialidadeTurnosAberta === item.key) {
			setEspecialidadeTurnosAberta(null);
			setMenuTurnosFlutuante(null);
			return;
		}

		const rect = elementoAncora.getBoundingClientRect();
		const larguraMenu = 160;
		const alturaMenu = Math.min(item.turnos.length * 34 + 12, 260);
		const margem = 8;
		const limiteNavbar = 88;
		const cabeADireita =
			rect.right + margem + larguraMenu <= window.innerWidth - margem;
		const left = cabeADireita
			? rect.right + margem
			: Math.max(margem, rect.left - larguraMenu - margem);
		const top = Math.min(
			Math.max(rect.top, limiteNavbar),
			Math.max(limiteNavbar, window.innerHeight - alturaMenu - margem),
		);

		setEspecialidadeTurnosAberta(item.key);
		setMenuTurnosFlutuante({
			key: item.key,
			turnos: item.turnos,
			left,
			top,
		});
	};

	useEffect(() => {
		if (!menuTurnosFlutuante) return undefined;

		const fecharMenuTurnos = () => {
			setEspecialidadeTurnosAberta(null);
			setMenuTurnosFlutuante(null);
		};

		window.addEventListener('resize', fecharMenuTurnos);
		window.addEventListener('scroll', fecharMenuTurnos, true);

		return () => {
			window.removeEventListener('resize', fecharMenuTurnos);
			window.removeEventListener('scroll', fecharMenuTurnos, true);
		};
	}, [menuTurnosFlutuante]);

	const getTipoKeyByItem = (item) => normalizarTexto(item?.tipo_item || '');

	const getTipoVisualKeyByItem = (item) => {
		const tipo = getTipoKeyByItem(item);
		return tipo === 'troca' ? 'alocacao' : tipo;
	};

	const getPeriodoTurno = useCallback(
		(linha = {}) => {
			const turno = normalizarTexto(
				`${linha.turnoKey || ''} ${linha.turnoNome || ''}`,
			);

			if (turno.includes('noite') || turno.includes('noturno'))
				return 'noite';
			if (turno.includes('tarde') || turno.includes('vespertino')) {
				return 'tarde';
			}
			return 'manha';
		},
		[normalizarTexto],
	);

	const getLinhaTurnoBackgroundClass = (linha = {}) => {
		const periodo = getPeriodoTurno(linha);

		if (periodo === 'noite') return 'bg-[#DDE3E8]';
		if (periodo === 'tarde') return 'bg-[#EEF1F4]';
		return 'bg-white';
	};

	const getTurnoBackgroundClass = () => 'bg-[#0B4D7A]';

	const formatarHora = (hora) => (hora ? hora.slice(0, 5) : '--:--');

	const getHorarioItem = (item) =>
		item?.horario || item?.resumo?.horario || item?.edicao?.horario || null;

	const buildDadosEdicaoItem = (
		item,
		celula,
		datasRepresentadas = [],
		linha = null,
	) => {
		const horarioItem = getHorarioItem(item) || {};
		const profissionalPorCpf = item?.edicao?.profissional_por_cpf || {};
		const contextoCompetencia = item?.edicao?.contexto_competencia || {};
		const vinculoItem = getVinculoItem(item, linha);
		const dataReferencia =
			horarioItem?.data ||
			item?.data ||
			item?.data_referencia ||
			datasRepresentadas?.[0] ||
			celula?.data ||
			'';

		return {
			id: item?.id || '',
			item_id: item?.id || '',
			tipo_item: item?.edicao?.tipo_item || item?.tipo_item || '',
			data: dataReferencia,
			data_inicio_vacancia:
				item?.edicao?.data_inicio_vacancia ||
				item?.data_inicio_vacancia ||
				dataReferencia ||
				'',
			nome_dia_semana:
				horarioItem?.nome_dia_semana || celula?.nome_dia_semana || '',
			dia_semana: horarioItem?.dia_semana || celula?.dia_semana || '',
			hora_inicio: horarioItem?.hora_inicio || item?.hora_inicio || '',
			hora_fim: horarioItem?.hora_fim || item?.hora_fim || '',
			faixa_horaria:
				horarioItem?.faixa_horaria ||
				item?.faixa_horaria ||
				linha?.turnoKey ||
				item?.nome_faixa_horaria ||
				'',
			nome_faixa_horaria:
				horarioItem?.nome_faixa_horaria ||
				item?.nome_faixa_horaria ||
				linha?.turnoNome ||
				item?.faixa_horaria ||
				'',
			observacao:
				item?.edicao?.observacao || item?.resumo?.observacao || '',
			profissional:
				item?.profissional?.id ||
				profissionalPorCpf?.cpf ||
				item?.profissional?.nome ||
				'',
			nome:
				item?.profissional?.nome ||
				profissionalPorCpf?.nome ||
				item?.resumo?.titulo ||
				'',
			nome_profissional:
				item?.profissional?.nome ||
				profissionalPorCpf?.nome ||
				item?.resumo?.titulo ||
				'',
			cpf: item?.profissional?.cpf || profissionalPorCpf?.cpf || '',
			cargo: profissionalPorCpf?.cargo || '',
			data_admissao: profissionalPorCpf?.data_admissao || '',
			telefone: profissionalPorCpf?.telefone || '',
			carga_horaria_semanal:
				profissionalPorCpf?.carga_horaria_semanal || '',
			especialidade_id:
				item?.especialidade?.id ||
				item?.especialidade_id ||
				profissionalPorCpf?.especialidade_id ||
				'',
			especialidade:
				item?.especialidade?.nome ||
				item?.nome_especialidade ||
				item?.resumo?.especialidade ||
				profissionalPorCpf?.especialidade_nome ||
				'',
			nome_especialidade:
				item?.nome_especialidade ||
				item?.especialidade?.nome ||
				item?.resumo?.especialidade ||
				profissionalPorCpf?.especialidade_nome ||
				'',
			vinculo_tipo:
				profissionalPorCpf?.vinculo_tipo_id ||
				item?.vinculo_tipo_id ||
				item?.vinculo?.id ||
				'',
			vinculo: vinculoItem,
			nome_vinculo: vinculoItem,
			regime: String(
				data?.contexto?.regime ||
					item?.edicao?.contexto_competencia?.regime ||
					'',
			).toLowerCase(),
			servico:
				data?.contexto?.tipo_secao?.id ||
				setorSelecionado?.tipo_secao ||
				'',
			setor:
				contextoCompetencia?.setor_id ||
				setorSelecionado?.id ||
				data?.contexto?.setor?.id ||
				'',
			sala: data?.contexto?.setor?.id || setorSelecionado?.id || '',
		};
	};

	const logDadosProfissionalPreenchimento = (
		item,
		celula,
		datasRepresentadas = [],
		linha = null,
	) => {
		const dadosEdicao = buildDadosEdicaoItem(
			item,
			celula,
			datasRepresentadas,
			linha,
		);

		/* console.log(
			'[ApiScaleGrid] Dados do profissional para preenchimento do modal:',
			{
				id: dadosEdicao.id,
				item_id: dadosEdicao.item_id,
				tipo_item: dadosEdicao.tipo_item,
				data: dadosEdicao.data,
				cpf: dadosEdicao.cpf,
				nome: dadosEdicao.nome,
				nome_profissional: dadosEdicao.nome_profissional,
				cargo: dadosEdicao.cargo,
				data_admissao: dadosEdicao.data_admissao,
				telefone: dadosEdicao.telefone,
				carga_horaria_semanal: dadosEdicao.carga_horaria_semanal,
				especialidade_id: dadosEdicao.especialidade_id,
				especialidade: dadosEdicao.especialidade,
				nome_especialidade: dadosEdicao.nome_especialidade,
				vinculo_tipo: dadosEdicao.vinculo_tipo,
				vinculo: dadosEdicao.vinculo,
				regime: dadosEdicao.regime,
				hora_inicio: dadosEdicao.hora_inicio,
				hora_fim: dadosEdicao.hora_fim,
				faixa_horaria: dadosEdicao.faixa_horaria,
				nome_faixa_horaria: dadosEdicao.nome_faixa_horaria,
				observacao: dadosEdicao.observacao,
				setor: dadosEdicao.setor,
				sala: dadosEdicao.sala,
				servico: dadosEdicao.servico,
			},
		); */

		return dadosEdicao;
	};

	const getTipoLabelByItem = (item) => {
		const tipo = getTipoVisualKeyByItem(item);

		if (tipo === 'vacancia') return 'VACÂNCIA';
		if (tipo === 'atestado') return 'ATESTADO';
		if (tipo === 'folga' || tipo === 'filga') return 'FOLGA';
		if (tipo === 'licenca' || tipo === 'licença') return 'LICENÇA';
		if (tipo === 'falta') return 'FALTA';
		if (tipo === 'ferias') return 'FÉRIAS';
		if (tipo === 'alocacao') return 'ALOCAÇÃO';

		return (item?.tipo_item || 'TIPO').toUpperCase();
	};

	const getStatusKeyByItem = (item) => {
		const tipoOriginal = getTipoKeyByItem(item);
		const tipo = getTipoVisualKeyByItem(item);

		if (tipoOriginal === 'troca') return 'TROCA';
		if (tipo === 'vacancia') return 'VACANCIA';
		if (tipo === 'ferias') return 'FERIAS';
		if (tipo === 'atestado') return 'ATESTADO';
		if (tipo === 'folga' || tipo === 'filga') return 'FOLGA';
		if (tipo === 'licenca' || tipo === 'licença') return 'LICENCA';
		if (tipo === 'falta') return 'FALTA';
		const tiposDataEspecifica = [
			'DATA_ESPECIFICA', 'DATAS_ESPECIFICAS',
			'DIA_ESPECIFICO', 'DIAS_ESPECIFICOS',
		];
		const isDataEspecifica = [
			item?.tipo_item,
			item?.tipo_recorrencia,
			item?.edicao?.tipo_recorrencia,
		].some((valor) =>
			tiposDataEspecifica.includes(String(valor || '').trim().toUpperCase()),
		);
		if (isDataEspecifica) return 'DATA_ESPECIFICA';
		if (tipo === 'alocacao') return 'ATRIBUIDO';

		return 'NAO_ATRIBUIDO';
	};

	const getStyleByItem = (item) => {
		const statusKey = getStatusKeyByItem(item);
		const isAlocacao = statusKey === 'ATRIBUIDO';
		const hasFilledBackground = !['NAO_ATRIBUIDO', 'BLOQUEADO'].includes(
			statusKey,
		);

		return {
			badgeClass: `${isAlocacao ? STATUS_CELL_CLASS.NAO_ATRIBUIDO : STATUS_CELL_CLASS[statusKey] || STATUS_CELL_CLASS.NAO_ATRIBUIDO} ${hasFilledBackground && !isAlocacao ? 'text-white' : 'text-[#65686d]'}`,
			typeTextClass: isAlocacao
				? 'text-[#65686d]'
				: STATUS_TEXT_CLASS[statusKey] ||
					STATUS_TEXT_CLASS.NAO_ATRIBUIDO,
		};
	};

	const getBadgeClassByItem = (item) => {
		return getStyleByItem(item).badgeClass;
	};

	const isViewOnlyMode = canManageItems && !canModifyItems;

	const itensPorId = useMemo(() => {
		const itensOriginais = Array.isArray(data?.itens) ? data.itens : [];
		const itensAninhados = (visualizacaoPayload?.setores || []).flatMap(
			(setor = {}) =>
				(setor?.linhas || []).flatMap((linha = {}) =>
					(linha?.celulas || []).flatMap(
						(celula = {}) => celula?.cards || [],
					),
				),
		);

		const todosItens =
			itensOriginais.length > 0 ? itensOriginais : itensAninhados;

		return Object.fromEntries(
			todosItens
				.filter((item) => item?.id !== undefined && item?.id !== null)
				.map((item) => [item.id, item]),
		);
	}, [data?.itens, visualizacaoPayload]);

	const visualizacaoSetores = useMemo(
		() => visualizacaoPayload?.setores || [],
		[visualizacaoPayload],
	);

	const escalaNaoEncontrada = useMemo(() => {
		if (payload == null) return false;

		const possuiGrade =
			Array.isArray(visualizacaoSetores) &&
			visualizacaoSetores.length > 0;
		const possuiColunas =
			Array.isArray(visualizacaoPayload?.colunas) &&
			visualizacaoPayload.colunas.length > 0;
		const possuiItens = Object.keys(itensPorId).length > 0;

		if (possuiGrade || possuiColunas || possuiItens) return false;

		return data?.contexto?.escala_mensal_id == null;
	}, [
		data?.contexto?.escala_mensal_id,
		itensPorId,
		payload,
		visualizacaoPayload,
		visualizacaoSetores,
	]);

	const colunasDoMes = useMemo(
		() => visualizacaoPayload?.colunas || [],
		[visualizacaoPayload],
	);

	const semanasDoMes = useMemo(
		() => buildSemanasDoMes(colunasDoMes),
		[colunasDoMes],
	);

	useEffect(() => {
		if (!semanasDoMes.length) {
			if (!initialWeekSelected.current) {
				setSemanaSelecionadaIndex(0);
			}
			return;
		}

		// Após a seleção inicial, apenas garante que o índice está dentro do range válido
		if (initialWeekSelected.current) {
			setSemanaSelecionadaIndex((prev) =>
				Math.min(prev, Math.max(semanasDoMes.length - 1, 0)),
			);
			return;
		}

		// Primeira carga: navega automaticamente para a semana atual
		initialWeekSelected.current = true;
		const hojeBrasiliaISO = getHojeBrasiliaISO();
		const indiceSemanaAtual = semanasDoMes.findIndex((semana) =>
			semana.some((coluna) => coluna?.data === hojeBrasiliaISO),
		);

		if (indiceSemanaAtual >= 0) {
			setSemanaSelecionadaIndex(indiceSemanaAtual);
			return;
		}

		setSemanaSelecionadaIndex(0);
	}, [semanasDoMes]);

	const semanaSelecionada = useMemo(
		() => semanasDoMes[semanaSelecionadaIndex] || semanasDoMes[0] || [],
		[semanasDoMes, semanaSelecionadaIndex],
	);

	const colunasSemana = useMemo(() => {
		if (modoMensal) return colunasDoMes;

		const porDia = new Map();

		semanaSelecionada.forEach((coluna) => {
			const diaNormalizado = normalizarDiaSemana(coluna?.nome_dia_semana);
			if (!diaNormalizado) return;
			porDia.set(diaNormalizado, coluna);
		});

		return DIAS_ORDEM.map(
			(dia) =>
				porDia.get(dia) || {
					nome_dia_semana: dia,
					data: '',
					dia_mes: '',
				},
		);
	}, [colunasDoMes, modoMensal, semanaSelecionada]);

	const setorSelecionado = useMemo(() => {
		if (!visualizacaoSetores.length) return null;

		const setorInicialId =
			visualizacaoPayload?.setor_inicial_id ||
			data?.grade?.setor_inicial_id ||
			data?.visualizacao?.setor_inicial_id ||
			data?.contexto?.setor?.id ||
			null;

		return (
			visualizacaoSetores.find(
				(setor) => String(setor?.id) === String(setorInicialId),
			) || visualizacaoSetores[0]
		);
	}, [
		data?.contexto?.setor?.id,
		data?.grade?.setor_inicial_id,
		data?.visualizacao?.setor_inicial_id,
		visualizacaoPayload,
		visualizacaoSetores,
	]);

	const nomeSetorEscala = useMemo(
		() =>
			tituloEscala ||
			data?.contexto?.setor?.nome_exibicao ||
			data?.contexto?.setor?.nome ||
			setorSelecionado?.nome_exibicao ||
			setorSelecionado?.nome ||
			'Setor',
		[data, setorSelecionado, tituloEscala],
	);

	useEffect(() => {
		if (!preservarAlturaNomeSetor) {
			setAlturaNomeSetorRenderizado(0);
			return undefined;
		}

		const medirNomeSetor = () => {
			const altura = Array.from(floatingLabelRefs.current).reduce(
				(maiorAltura, label) => {
					if (!label?.isConnected || label.dataset.setorLabel !== 'true') {
						return maiorAltura;
					}

					return Math.max(
						maiorAltura,
						Math.ceil(label.scrollHeight || label.offsetHeight || 0),
					);
				},
				0,
			);

			setAlturaNomeSetorRenderizado((anterior) =>
				anterior === altura ? anterior : altura,
			);
		};

		const animationFrame = window.requestAnimationFrame(medirNomeSetor);
		return () => window.cancelAnimationFrame(animationFrame);
	}, [nomeSetorEscala, preservarAlturaNomeSetor, visualizacaoPayload]);

	const totalLeitosEscala = useMemo(() => {
		const quantidadeSetor = Number(setorSelecionado?.quantidade_leitos);

		if (Number.isFinite(quantidadeSetor) && quantidadeSetor > 0) {
			return quantidadeSetor;
		}

		const mapaSalas = new Map();
		const linhasSetor = Array.isArray(setorSelecionado?.linhas)
			? setorSelecionado.linhas
			: [];

		linhasSetor.forEach((linha) => {
			const salaId = linha?.sala?.id;
			const quantidadeSala = Number(linha?.sala?.quantidade_leitos ?? 0);

			if (!salaId || !Number.isFinite(quantidadeSala)) return;
			if (mapaSalas.has(salaId)) return;
			mapaSalas.set(salaId, quantidadeSala);
		});

		return Array.from(mapaSalas.values()).reduce(
			(total, quantidade) => total + quantidade,
			0,
		);
	}, [setorSelecionado]);

	const capacidadeLabel = useMemo(() => {
		const quantidadeCapacidade = Number(
			setorSelecionado?.quantidade_capacidade ??
				setorSelecionado?.quantidade_leitos,
		);
		const tipoRaw = String(setorSelecionado?.tipo_capacidade || 'LEITO')
			.trim()
			.toUpperCase();
		const tipoPlural = tipoRaw.endsWith('S') ? tipoRaw : `${tipoRaw}S`;
		const quantidadeVal =
			Number.isFinite(quantidadeCapacidade) && quantidadeCapacidade > 0
				? quantidadeCapacidade
				: totalLeitosEscala || 0;
		const tipoFinal =
			Number.isFinite(quantidadeCapacidade) && quantidadeCapacidade > 0
				? tipoPlural
				: 'LEITOS';
		return `${quantidadeVal} ${tipoFinal}`;
	}, [setorSelecionado, totalLeitosEscala]);

	const ultimaAtualizacaoEscala = useMemo(
		() =>
			data?.resumo?.ultima_atualizacao || data?.ultima_atualizacao || '',
		[data],
	);

	const legendaCards = useMemo(
		() => [
			{ label: 'Férias', className: 'text-[#6ACEF8]' },
			{ label: 'Atestado', className: 'text-[#FB965C]' },
			{ label: 'Folga', className: 'text-[#8BBD8B]' },
			{ label: 'Licença', className: 'text-[#A476C7]' },
			{ label: 'Falta', className: 'text-[#545454]' },
			{ label: 'Troca', className: 'text-[#967771]' },
			{ label: 'Dia específico', className: 'text-[#e685db]' },
			{ label: 'Vacância', className: 'text-[#BD504E]' },
		],
		[],
	);

	const linhas = useMemo(() => {
		const linhasOriginais = Array.isArray(setorSelecionado?.linhas)
			? setorSelecionado.linhas
			: [];
		const turnosBasicos = [
			{
				turnoKey: 'MANHA',
				turnoNome: 'ManhÃ£',
				ordem: 1,
				periodo: 'manha',
			},
			{
				turnoKey: 'TARDE',
				turnoNome: 'Tarde',
				ordem: 2,
				periodo: 'tarde',
			},
			{
				turnoKey: 'NOITE',
				turnoNome: 'Noite',
				ordem: 3,
				periodo: 'noite',
			},
		];
		const turnosPlantonista = [
			{
				turnoKey: 'DIURNO',
				turnoNome: 'Diurno',
				ordem: 1,
				regimeGrupo: 'plantonista',
			},
			{
				turnoKey: 'NOTURNO',
				turnoNome: 'Noturno',
				ordem: 2,
				regimeGrupo: 'plantonista',
			},
		];
		const turnosRotina = turnosBasicos.map((turno, index) => ({
			...turno,
			ordem: 101 + index,
			regimeGrupo: 'rotina',
		}));
		const regimesSelecionados =
			regimeNormalizado === 'todos' ||
			regimeNormalizado === 'todo' ||
			regimeNormalizado === 'all'
				? ['plantonista']
				: regimeNormalizado === 'plantonista'
					? ['plantonista']
					: regimeNormalizado === 'rotina'
						? ['rotina']
						: [];
		const isRegimeTodos =
			regimeNormalizado === 'todos' ||
			regimeNormalizado === 'todo' ||
			regimeNormalizado === 'all';
		const turnosEsperados = regimesSelecionados.flatMap((regimeItem) =>
			regimeItem === 'plantonista' ? turnosPlantonista : turnosRotina,
		);
		const getRegimeGrupoLinha = (linha = {}) => {
			const regimeLinha = normalizarTexto(
				linha.__regimeGrupo ||
					linha.regime ||
					linha?.contexto?.regime ||
					linha?.contexto_competencia?.regime ||
					linha?.faixa_horaria?.regime ||
					'',
			);

			if (regimeLinha.includes('plant')) return 'plantonista';
			if (regimeLinha.includes('rotina')) return 'rotina';

			const turno = normalizarTexto(
				`${linha?.turnoKey || linha?.faixa_horaria?.codigo || ''} ${
					linha?.turnoNome || linha?.faixa_horaria?.nome || ''
				}`,
			);

			if (turno.includes('diurno') || turno.includes('noturno')) {
				return 'plantonista';
			}
			return 'rotina';
		};
		const getTurnoAgrupadoTodos = (linha = {}) => {
			const regimeGrupo = getRegimeGrupoLinha(linha);
			const turnoKeyOriginal =
				linha?.turnoKey || linha?.faixa_horaria?.codigo || 'turno';
			const turnoNomeOriginal =
				linha?.turnoNome || linha?.faixa_horaria?.nome || 'Turno';

			if (!isRegimeTodos) {
				return {
					turnoKey: turnoKeyOriginal,
					turnoNome: turnoNomeOriginal,
					regimeGrupo,
				};
			}

			const turnoNormalizado = normalizarTexto(
				`${turnoKeyOriginal} ${turnoNomeOriginal}`,
			);
			const isNoturno =
				turnoNormalizado.includes('noite') ||
				turnoNormalizado.includes('noturno');

			return {
				turnoKey: isNoturno ? 'NOTURNO' : 'DIURNO',
				turnoNome: isNoturno ? 'Noturno' : 'Diurno',
				regimeGrupo: 'plantonista',
			};
		};
		const getTurnoMapaKey = (linha = {}) =>
			`${getTurnoAgrupadoTodos(linha).regimeGrupo}:${
				getTurnoAgrupadoTodos(linha).turnoKey
			}`;
		const getTurno24hKeyLinha = (linha = {}) => {
			const turno = normalizarTexto(
				`${linha?.turnoKey || linha?.faixa_horaria?.codigo || ''} ${
					linha?.turnoNome || linha?.faixa_horaria?.nome || ''
				}`,
			);

			if (turno.includes('noturno') || turno.includes('noite')) {
				return 'NOTURNO';
			}
			if (
				turno.includes('diurno') ||
				turno.includes('manha') ||
				turno.includes('tarde') ||
				turno.includes('vespertino')
			) {
				return 'DIURNO';
			}
			return '';
		};
		const isRegimeComSeparacao24h = (regimeGrupo = '') =>
			regimeGrupo === 'plantonista' || regimeGrupo === 'rotina';
		const getHoraItemRenderizacao = (
			item = {},
			celula = {},
			linha = {},
		) => {
			const horarioItem = getHorarioItem(item) || {};
			return {
				hora_inicio:
					horarioItem?.hora_inicio ||
					item?.hora_inicio ||
					celula?.hora_inicio ||
					linha?.regime_escala?.hora_inicio ||
					linha?.hora_inicio ||
					'',
				hora_fim:
					horarioItem?.hora_fim ||
					item?.hora_fim ||
					celula?.hora_fim ||
					linha?.regime_escala?.hora_fim ||
					linha?.hora_fim ||
					'',
			};
		};
		const isPlantao24hRenderizacao = (
			item = {},
			celula = {},
			linha = {},
		) => {
			if (!isRegimeComSeparacao24h(getRegimeGrupoLinha(linha))) {
				return false;
			}
			const { hora_inicio, hora_fim } = getHoraItemRenderizacao(
				item,
				celula,
				linha,
			);
			const horaInicio = String(hora_inicio || '').slice(0, 5);
			const horaFim = String(hora_fim || '').slice(0, 5);

			return Boolean(horaInicio && horaFim && horaInicio === horaFim);
		};
		const getHoraInicioLinhaTurno = (linha = {}) =>
			String(
				linha?.regime_escala?.hora_inicio ||
					linha?.faixa_horaria?.hora_inicio ||
					linha?.hora_inicio ||
					'',
			).slice(0, 5);
		const horaParaMinutos = (hora = '') => {
			const [horas, minutos] = String(hora || '')
				.slice(0, 5)
				.split(':')
				.map((parte) => Number(parte));
			if (!Number.isFinite(horas)) return null;
			return horas * 60 + (Number.isFinite(minutos) ? minutos : 0);
		};
		const somarDiasDataISO = (dataISO = '', dias = 0) => {
			if (!dataISO) return '';
			const dataBase = new Date(`${dataISO}T00:00:00`);
			if (Number.isNaN(dataBase.getTime())) return '';
			dataBase.setDate(dataBase.getDate() + dias);
			return dataBase.toISOString().slice(0, 10);
		};
		const ajustarItemPlantao24hParaTurno = ({
			item,
			celula,
			linha,
			turnoKey,
			limiteDiurno,
			limiteNoturno,
			horarioRenderizacaoOverride = null,
			isDuplicado = false,
		}) => {
			const { hora_inicio, hora_fim } = getHoraItemRenderizacao(
				item,
				celula,
				linha,
			);
			const horaInicioOriginal = String(hora_inicio || '').slice(0, 5);
			const horaFimOriginal = String(hora_fim || '').slice(0, 5);

			if (!horaInicioOriginal || !horaFimOriginal) return item;

			const inicioMinutos = horaParaMinutos(horaInicioOriginal);
			const limiteNoturnoMinutos = horaParaMinutos(limiteNoturno);
			const iniciaNoPeriodoNoturno =
				inicioMinutos !== null &&
				limiteNoturnoMinutos !== null &&
				inicioMinutos >= limiteNoturnoMinutos;

			let horarioRenderizacao = horarioRenderizacaoOverride;

			if (!horarioRenderizacao) {
				horarioRenderizacao =
					turnoKey === 'NOTURNO'
						? iniciaNoPeriodoNoturno
							? {
									hora_inicio: horaInicioOriginal,
									hora_fim: limiteDiurno,
								}
							: {
									hora_inicio: limiteNoturno,
									hora_fim: horaFimOriginal,
								}
						: iniciaNoPeriodoNoturno
							? {
									hora_inicio: limiteDiurno,
									hora_fim: horaFimOriginal,
								}
							: {
									hora_inicio: horaInicioOriginal,
									hora_fim: limiteNoturno,
								};
			}

			return {
				...item,
				...(isDuplicado ? { __plantao24hDuplicado: true } : {}),
				__plantao24hRenderizacao: true,
				__horarioPlantao24hRenderizacao: horarioRenderizacao,
			};
		};
		const duplicarPlantoes24hEntreTurnos = (linhasBase = []) => {
			const linhasClonadas = linhasBase.map((linha) => ({
				...linha,
				celulas: Array.isArray(linha?.celulas)
					? linha.celulas.map((celula) => ({
							...celula,
							cards: Array.isArray(celula?.cards)
								? [...celula.cards]
								: celula?.cards,
							itens_ids: Array.isArray(celula?.itens_ids)
								? [...celula.itens_ids]
								: celula?.itens_ids,
						}))
					: [],
			}));
			const linhasPorTurno = new Map();
			const duplicacoes = [];

			linhasClonadas.forEach((linha) => {
				const regimeGrupoLinha = getRegimeGrupoLinha(linha);
				if (!isRegimeComSeparacao24h(regimeGrupoLinha)) return;
				const turnoKey = getTurno24hKeyLinha(linha);
				if (!turnoKey) return;
				const mapaKey = `${regimeGrupoLinha}:${turnoKey}`;
				if (!linhasPorTurno.has(mapaKey)) {
					linhasPorTurno.set(mapaKey, linha);
				}
			});
			const getLimitesPorRegime = (regimeGrupo = '') => ({
				limiteDiurno:
					getHoraInicioLinhaTurno(
						linhasPorTurno.get(`${regimeGrupo}:DIURNO`),
					) || '07:00',
				limiteNoturno:
					getHoraInicioLinhaTurno(
						linhasPorTurno.get(`${regimeGrupo}:NOTURNO`),
					) || '19:00',
			});
			const criarHorariosSegmentos24h = ({
				horaInicioOriginal,
				horaFimOriginal,
				limiteDiurno,
				limiteNoturno,
			}) => {
				const inicioMinutos = horaParaMinutos(horaInicioOriginal);
				const limiteNoturnoMinutos = horaParaMinutos(limiteNoturno);
				const iniciaNoPeriodoNoturno =
					inicioMinutos !== null &&
					limiteNoturnoMinutos !== null &&
					inicioMinutos >= limiteNoturnoMinutos;

				if (iniciaNoPeriodoNoturno) {
					return {
						origem: {
							hora_inicio: horaInicioOriginal,
							hora_fim: limiteDiurno,
						},
						mesmoDiaOutroTurno: null,
						proximoDiaOutroTurno: {
							hora_inicio: limiteDiurno,
							hora_fim: horaFimOriginal,
						},
					};
				}

				return {
					origem: {
						hora_inicio: horaInicioOriginal,
						hora_fim: limiteNoturno,
					},
					mesmoDiaOutroTurno: {
						hora_inicio: limiteNoturno,
						hora_fim: horaFimOriginal,
					},
					proximoDiaOutroTurno: null,
				};
			};
			const inserirSegmentoPlantao24h = ({
				item,
				id,
				celula,
				dataDestino,
				regimeGrupoDestino,
				turnoDestino,
				limiteDiurno,
				limiteNoturno,
				horarioRenderizacao,
			}) => {
				if (!dataDestino || !turnoDestino || !horarioRenderizacao)
					return;

				const linhaDestino = linhasPorTurno.get(
					`${regimeGrupoDestino}:${turnoDestino}`,
				);
				if (!linhaDestino) return;

				let celulaDestino = (linhaDestino.celulas || []).find(
					(celulaAtual) =>
						String(celulaAtual?.data || '') === String(dataDestino),
				);

				if (!celulaDestino) {
					celulaDestino = {
						...celula,
						data: dataDestino,
						cards: [],
						itens_ids: [],
					};
					linhaDestino.celulas = [
						...(linhaDestino.celulas || []),
						celulaDestino,
					];
				}

				const destinoCards = Array.isArray(celulaDestino.cards)
					? celulaDestino.cards
					: [];
				const segmentKey = `${id || 'item'}-${dataDestino}-${turnoDestino}-${horarioRenderizacao.hora_inicio}-${horarioRenderizacao.hora_fim}`;
				const jaExiste = destinoCards.some(
					(card) => card?.__plantao24hSegmentKey === segmentKey,
				);

				if (!jaExiste) {
					destinoCards.push(
						ajustarItemPlantao24hParaTurno({
							item,
							celula,
							linha: linhaDestino,
							turnoKey: turnoDestino,
							limiteDiurno,
							limiteNoturno,
							horarioRenderizacaoOverride: horarioRenderizacao,
							isDuplicado: true,
						}),
					);
					destinoCards[
						destinoCards.length - 1
					].__plantao24hSegmentKey = segmentKey;
				}

				celulaDestino.cards = destinoCards;
			};

			linhasClonadas.forEach((linha) => {
				const regimeGrupoLinha = getRegimeGrupoLinha(linha);
				if (!isRegimeComSeparacao24h(regimeGrupoLinha)) return;
				const { limiteDiurno, limiteNoturno } =
					getLimitesPorRegime(regimeGrupoLinha);

				const turnoOrigem = getTurno24hKeyLinha(linha);
				const turnoDestino =
					turnoOrigem === 'DIURNO'
						? 'NOTURNO'
						: turnoOrigem === 'NOTURNO'
							? 'DIURNO'
							: '';

				if (!turnoDestino) return;

				(linha?.celulas || []).forEach((celula) => {
					const cardsOriginais =
						Array.isArray(celula?.cards) && celula.cards.length > 0
							? celula.cards
							: (celula?.itens_ids || [])
									.map((id) => itensPorId[id])
									.filter(Boolean);
					const cards = cardsOriginais.map((item) => {
						if (!isPlantao24hRenderizacao(item, celula, linha)) {
							return item;
						}

						const { hora_inicio, hora_fim } =
							getHoraItemRenderizacao(item, celula, linha);
						const segmentos = criarHorariosSegmentos24h({
							horaInicioOriginal: String(hora_inicio || '').slice(
								0,
								5,
							),
							horaFimOriginal: String(hora_fim || '').slice(0, 5),
							limiteDiurno,
							limiteNoturno,
						});

						return ajustarItemPlantao24hParaTurno({
							item,
							celula,
							linha,
							turnoKey: turnoOrigem,
							limiteDiurno,
							limiteNoturno,
							horarioRenderizacaoOverride: segmentos.origem,
						});
					});

					if (cards.length > 0) {
						celula.cards = cards;
					}

					cards.forEach((item) => {
						if (!isPlantao24hRenderizacao(item, celula, linha))
							return;

						duplicacoes.push({
							item,
							id: item?.id,
							celula,
							linhaOrigem: linha,
							regimeGrupoLinha,
							turnoOrigem,
							turnoDestino,
						});
					});
				});
			});

			duplicacoes.forEach(
				({
					item,
					id,
					celula,
					linhaOrigem,
					regimeGrupoLinha,
					turnoOrigem,
					turnoDestino,
				}) => {
					const { limiteDiurno, limiteNoturno } =
						getLimitesPorRegime(regimeGrupoLinha);
					const dataCelula = String(celula?.data || '');
					const { hora_inicio, hora_fim } = getHoraItemRenderizacao(
						item,
						celula,
						linhaOrigem,
					);
					const segmentos = criarHorariosSegmentos24h({
						horaInicioOriginal: String(hora_inicio || '').slice(
							0,
							5,
						),
						horaFimOriginal: String(hora_fim || '').slice(0, 5),
						limiteDiurno,
						limiteNoturno,
					});
					const dataProximoDia = somarDiasDataISO(dataCelula, 1);

					if (segmentos.mesmoDiaOutroTurno) {
						inserirSegmentoPlantao24h({
							item,
							id,
							celula,
							dataDestino: dataCelula,
							regimeGrupoDestino: regimeGrupoLinha,
							turnoDestino,
							limiteDiurno,
							limiteNoturno,
							horarioRenderizacao: segmentos.mesmoDiaOutroTurno,
						});
					}

					if (segmentos.proximoDiaOutroTurno) {
						inserirSegmentoPlantao24h({
							item,
							id,
							celula,
							dataDestino: dataProximoDia,
							regimeGrupoDestino: regimeGrupoLinha,
							turnoDestino,
							limiteDiurno,
							limiteNoturno,
							horarioRenderizacao: segmentos.proximoDiaOutroTurno,
						});
					}
				},
			);

			return linhasClonadas;
		};
		const criarLinhaTurnoVazia = (turnoModelo) => ({
			faixa_horaria: {
				codigo: turnoModelo.turnoKey,
				nome: turnoModelo.turnoNome,
				regime: turnoModelo.regimeGrupo,
			},
			regime: turnoModelo.regimeGrupo,
			__regimeGrupo: turnoModelo.regimeGrupo,
			ordem: turnoModelo.ordem,
			linha_id: `turno-vazio-${turnoModelo.regimeGrupo}-${turnoModelo.turnoKey}`,
			celulas: [],
		});
		const completarTurnosBase = (linhasBase = []) => {
			if (!deveGarantirTurnosBasicos || turnosEsperados.length === 0) {
				return linhasBase;
			}

			const linhasCompletas = [...linhasBase];

			turnosEsperados.forEach((turnoModelo) => {
				const jaExiste = linhasCompletas.some(
					(linha) =>
						getRegimeGrupoLinha(linha) ===
							turnoModelo.regimeGrupo &&
						normalizarTexto(linha.turnoKey) ===
							normalizarTexto(turnoModelo.turnoKey),
				);

				if (jaExiste) return;

				linhasCompletas.push({
					nomeSetorEscala,
					setorNome:
						setorSelecionado?.nome_exibicao ||
						setorSelecionado?.nome ||
						'Setor',
					turnoNome: turnoModelo.turnoNome,
					turnoKey: turnoModelo.turnoKey,
					ordem: turnoModelo.ordem,
					linhaKey: `turno-vazio-${turnoModelo.regimeGrupo}-${turnoModelo.turnoKey}`,
					celulas: [],
					__regimeGrupo: turnoModelo.regimeGrupo,
					__linhaSemCards: true,
				});
			});

			return linhasCompletas;
		};
		const deveFiltrarRegimeUnico =
			regimeNormalizado === 'plantonista' ||
			regimeNormalizado === 'rotina';
		const linhasVisiveisBase = deveFiltrarRegimeUnico
			? linhasOriginais.filter(
					(linha) => getRegimeGrupoLinha(linha) === regimeNormalizado,
				)
			: linhasOriginais;
		const linhasVisiveis =
			duplicarPlantoes24hEntreTurnos(linhasVisiveisBase);

		let base = linhasVisiveis.map((linha) => {
			const turnoAgrupado = getTurnoAgrupadoTodos(linha);

			return {
				nomeSetorEscala,
				setorNome:
					setorSelecionado?.nome_exibicao ||
					setorSelecionado?.nome ||
					'Setor',
				turnoNome: turnoAgrupado.turnoNome,
				turnoKey: turnoAgrupado.turnoKey,
				ordem: Number(linha.ordem ?? 0),
				linhaKey:
					linha.linha_id ||
					`${setorSelecionado?.id || 's'}-${linha.ordem || 'l'}`,
				celulas: linha.celulas || [],
				__regimeGrupo: turnoAgrupado.regimeGrupo,
			};
		});

		if (agruparPorEspecialidade) {
			const turnos = new Map();
			const getNomeAgrupamento = (
				item,
				linha,
				incluirSemVinculo = true,
			) => {
				const nome =
					agrupamentoAtivo === 'vinculo'
						? getVinculoItem(item, linha)
						: getEspecialidadeItem(item, linha);

				if (String(nome || '').trim()) return String(nome).trim();
				return agrupamentoAtivo === 'vinculo' && incluirSemVinculo
					? 'Sem vínculo'
					: '';
			};

			linhasVisiveis.forEach((linha, linhaIndex) => {
				const turnoAgrupado = getTurnoAgrupadoTodos(linha);
				const { turnoKey, turnoNome, regimeGrupo } = turnoAgrupado;
				const turnoMapaKey = getTurnoMapaKey({
					...linha,
					turnoKey,
					turnoNome,
					__regimeGrupo: regimeGrupo,
				});
				const turnoAtual = turnos.get(turnoMapaKey) || {
					turnoKey,
					turnoNome,
					ordem: Number(linha?.ordem ?? linhaIndex),
					regimeGrupo,
					linhas: [],
					agrupamentos: new Map(),
				};
				const agrupamentoLinha = getNomeAgrupamento({}, linha, false);

				turnoAtual.linhas.push(linha);
				if (agrupamentoLinha) {
					turnoAtual.agrupamentos.set(
						normalizarTexto(agrupamentoLinha),
						agrupamentoLinha,
					);
				}

				(linha?.celulas || []).forEach((celula) => {
					const cards =
						Array.isArray(celula?.cards) && celula.cards.length > 0
							? celula.cards
							: (celula?.itens_ids || [])
									.map((id) => itensPorId[id])
									.filter(Boolean);

					cards.forEach((item) => {
						const nomeAgrupamento = getNomeAgrupamento(item, linha);

						if (nomeAgrupamento) {
							turnoAtual.agrupamentos.set(
								normalizarTexto(nomeAgrupamento),
								nomeAgrupamento,
							);
						}
					});
				});

				turnos.set(turnoMapaKey, turnoAtual);
			});

			if (deveGarantirTurnosBasicos) {
				turnosEsperados.forEach((turnoModelo) => {
					const jaExiste = Array.from(turnos.values()).some(
						(turno) =>
							turno.regimeGrupo === turnoModelo.regimeGrupo &&
							normalizarTexto(turno.turnoKey) ===
								normalizarTexto(turnoModelo.turnoKey),
					);

					if (jaExiste) return;

					turnos.set(
						`${turnoModelo.regimeGrupo}:${turnoModelo.turnoKey}`,
						{
							turnoKey: turnoModelo.turnoKey,
							turnoNome: turnoModelo.turnoNome,
							ordem: turnoModelo.ordem,
							regimeGrupo: turnoModelo.regimeGrupo,
							linhas: [criarLinhaTurnoVazia(turnoModelo)],
							agrupamentos: new Map(),
							__turnoSintetico: true,
						},
					);
				});
			}

			const temAgrupamentoReal = Array.from(turnos.values()).some(
				(turno) => turno.agrupamentos.size > 0,
			);

			if (temAgrupamentoReal) {
				base = Array.from(turnos.values()).flatMap((turno) => {
					const agrupamentosTurno =
						turno.agrupamentos.size > 0
							? Array.from(turno.agrupamentos.entries())
							: deveGarantirTurnosBasicos
								? [[`sem-registros-${turno.turnoKey}`, '']]
								: [];

					return agrupamentosTurno
						.sort(([, nomeA], [, nomeB]) =>
							nomeA.localeCompare(nomeB, 'pt-BR'),
						)
						.map(
							(
								[agrupamentoKey, agrupamentoNome],
								agrupamentoIndex,
							) => {
								const celulasPorData = new Map();

								turno.linhas.forEach((linhaOrigem) => {
									(linhaOrigem?.celulas || []).forEach(
										(celula) => {
											const cards =
												Array.isArray(celula?.cards) &&
												celula.cards.length > 0
													? celula.cards
													: (celula?.itens_ids || [])
															.map(
																(id) =>
																	itensPorId[
																		id
																	],
															)
															.filter(Boolean);
											const cardsAgrupamento = cards
												.filter((item) => {
													const nomeAgrupamento =
														getNomeAgrupamento(
															item,
															linhaOrigem,
														);
													return (
														normalizarTexto(
															nomeAgrupamento,
														) === agrupamentoKey
													);
												})
												.map((item) => ({
													...item,
													__linhaOrigem: linhaOrigem,
												}));

											if (cardsAgrupamento.length === 0)
												return;

											const dataCelula = String(
												celula?.data || '',
											);
											const celulaAgrupada =
												celulasPorData.get(
													dataCelula,
												) || {
													...celula,
													cards: [],
													itens_ids: [],
												};

											celulaAgrupada.cards.push(
												...cardsAgrupamento,
											);
											celulaAgrupada.itens_ids.push(
												...cardsAgrupamento
													.map((item) => item?.id)
													.filter(
														(id) =>
															id !== undefined &&
															id !== null,
													),
											);
											celulasPorData.set(
												dataCelula,
												celulaAgrupada,
											);
										},
									);
								});

								return {
									nomeSetorEscala,
									setorNome:
										setorSelecionado?.nome_exibicao ||
										setorSelecionado?.nome ||
										'Setor',
									turnoNome: turno.turnoNome,
									turnoKey: turno.turnoKey,
									ordem: turno.ordem,
									linhaKey: `${turno.regimeGrupo}-${turno.turnoKey}-${agrupamentoAtivo}-${agrupamentoKey}-${agrupamentoIndex}`,
									celulas: Array.from(
										celulasPorData.values(),
									),
									agrupamentoNome,
									__regimeGrupo: turno.regimeGrupo,
									__temAgrupamentoReal:
										Boolean(agrupamentoNome),
									__linhaSemCards: !agrupamentoNome,
								};
							},
						);
				});
			} else {
				base = completarTurnosBase(base);
			}
		} else {
			base = completarTurnosBase(base);
		}

		if (base.length === 0) {
			return [
				{
					nomeSetorEscala,
					setorNome: 'Setor',
					turnoNome: 'Turno',
					turnoKey: 'turno',
					ordem: 1,
					linhaKey: 'fallback-1',
					celulas: [],
					especialidadeNome: 'Não informada',
					__temEspecialidadeReal: false,
				},
				{
					nomeSetorEscala,
					setorNome: 'Setor',
					turnoNome: 'Turno',
					turnoKey: 'turno',
					ordem: 2,
					linhaKey: 'fallback-2',
					celulas: [],
					especialidadeNome: 'Não informada',
					__temEspecialidadeReal: false,
				},
			];
		}

		const TURNO_ORDEM = ['DIURNO', 'NOTURNO', 'MANHA', 'TARDE', 'NOITE'];
		const REGIME_ORDEM = {
			plantonista: 0,
			rotina: 1,
		};
		const getTurnoOrdem = (key = '', nome = '', regimeGrupo = '') => {
			const candidatos = [key, nome];
			let melhorIndice = TURNO_ORDEM.length;

			candidatos.forEach((valor) => {
				const normalized = String(valor || '')
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toUpperCase()
					.trim();
				const idx = TURNO_ORDEM.indexOf(normalized);
				if (idx !== -1 && idx < melhorIndice) {
					melhorIndice = idx;
				}
			});

			const regimeOffset =
				REGIME_ORDEM[regimeGrupo] !== undefined
					? REGIME_ORDEM[regimeGrupo] * 100
					: 200;

			return regimeOffset + melhorIndice;
		};

		const linhasOrdenadas = base.sort((a, b) => {
			const ordemA = getTurnoOrdem(
				a.turnoKey,
				a.turnoNome,
				a.__regimeGrupo,
			);
			const ordemB = getTurnoOrdem(
				b.turnoKey,
				b.turnoNome,
				b.__regimeGrupo,
			);
			if (ordemA !== ordemB) {
				return ordemA - ordemB;
			}
			if (a.turnoKey === b.turnoKey) {
				return a.ordem - b.ordem;
			}
			return String(a.turnoKey).localeCompare(String(b.turnoKey));
		});

		if (!agruparPorEspecialidade) return linhasOrdenadas;
		if (!linhasOrdenadas.some((linha) => linha.__temAgrupamentoReal)) {
			return linhasOrdenadas;
		}

		const iconesUsados = new Set();
		const visualPorAgrupamento = new Map();
		let proximoIndiceVisual = 0;
		const paletteOffset = agrupamentoAtivo === 'vinculo' ? 6 : 0;

		return linhasOrdenadas.map((linha) => {
			if (!linha.__temAgrupamentoReal) return linha;

			const agrupamentoNome =
				linha.agrupamentoNome ||
				(agrupamentoAtivo === 'vinculo'
					? 'Sem vínculo'
					: 'Não informada');
			const agrupamentoKey = normalizarTexto(agrupamentoNome);

			if (!visualPorAgrupamento.has(agrupamentoKey)) {
				visualPorAgrupamento.set(
					agrupamentoKey,
					getSpecialtyVisual(
						agrupamentoNome,
						proximoIndiceVisual,
						iconesUsados,
						paletteOffset,
					),
				);
				proximoIndiceVisual += 1;
			}

			return {
				...linha,
				...visualPorAgrupamento.get(agrupamentoKey),
			};
		});
	}, [
		agruparPorEspecialidade,
		agrupamentoAtivo,
		deveGarantirTurnosBasicos,
		itensPorId,
		normalizarTexto,
		regimeNormalizado,
		setorSelecionado,
		nomeSetorEscala,
	]);

	const celulasPorDiaELinha = useMemo(() => {
		const mapa = {};

		colunasSemana.forEach((coluna) => {
			linhas.forEach((linha) => {
				const dataColuna = coluna?.data || '';
				const key = `${dataColuna || coluna.nome_dia_semana}-${linha.linhaKey}`;

				if (!dataColuna) {
					mapa[key] = [];
					return;
				}

				const celula = (linha.celulas || []).find(
					(itemCelula) => itemCelula?.data === dataColuna,
				);

				if (!celula) {
					mapa[key] = [];
					return;
				}

				const itensDaCelula =
					celula.cards && celula.cards.length > 0
						? celula.cards
						: (celula.itens_ids || [])
								.map((id) => itensPorId[id])
								.filter(Boolean);

				mapa[key] = itensDaCelula.map((item) => ({
					item,
					celula,
					datasRepresentadas: [
						extrairDataReal(item, celula) || dataColuna,
					],
				}));
			});
		});

		return mapa;
	}, [linhas, itensPorId, colunasSemana]);

	// A API de enfermagem entrega as categorias do mês inteiro. Na visão
	// semanal, uma categoria sem cards não deve continuar ocupando uma linha
	// (nem exibir uma aba que não possui ação naquela semana). Mantemos, porém,
	// uma linha vazia por turno quando ele não tiver nenhum card: assim o turno
	// Noturno continua visível e marca o início da sua faixa escura.
	const linhasRenderizadas = useMemo(() => {
		if (!preservarAlturaNomeSetor) return linhas;

		const possuiCardsNaSemana = (linha) =>
			colunasSemana.some((colunaDia) => {
				const key = `${colunaDia.data || colunaDia.nome_dia_semana}-${linha.linhaKey}`;
				return (celulasPorDiaELinha[key] || []).length > 0;
			});

		const linhasPorTurno = new Map();
		linhas.forEach((linha) => {
			const linhasDoTurno = linhasPorTurno.get(linha.turnoKey) || [];
			linhasDoTurno.push(linha);
			linhasPorTurno.set(linha.turnoKey, linhasDoTurno);
		});

		return Array.from(linhasPorTurno.values()).flatMap((linhasDoTurno) => {
			const linhasComCards = linhasDoTurno.filter(
				(linha) => !linha.__temAgrupamentoReal || possuiCardsNaSemana(linha),
			);

			if (linhasComCards.length > 0) return linhasComCards;

			const linhaBase = linhasDoTurno[0];
			return linhaBase
				? [
						{
							...linhaBase,
							agrupamentoNome: '',
							__temAgrupamentoReal: false,
							__linhaSemCards: true,
						},
					]
				: [];
		});
	}, [celulasPorDiaELinha, colunasSemana, linhas, preservarAlturaNomeSetor]);

	useEffect(() => {
		if (!preservarAlturaNomeSetor) return;

		const quantidadeCategoriasPorTurno = linhasRenderizadas.reduce(
			(mapa, linha) => {
				if (!linha.__temAgrupamentoReal) return mapa;
				mapa.set(linha.turnoKey, (mapa.get(linha.turnoKey) || 0) + 1);
				return mapa;
			}, new Map());
		const chavesParaAbrir = linhasRenderizadas
			.filter(
				(linha) =>
					linha.__temAgrupamentoReal &&
					quantidadeCategoriasPorTurno.get(linha.turnoKey) === 1,
			)
			.map((linha) => {
				const nomeAgrupamento =
					linha.agrupamentoNome ||
					(agrupamentoAtivo === 'vinculo'
						? 'Sem vínculo'
						: 'Não informada');
				return `${linha.turnoKey}-${normalizarTexto(nomeAgrupamento)}`;
			});

		if (chavesParaAbrir.length === 0) return;

		setAgrupamentosColapsados((anteriores) => {
			let alterou = false;
			const proximos = { ...anteriores };

			chavesParaAbrir.forEach((chave) => {
				if (proximos[chave]) {
					proximos[chave] = false;
					alterou = true;
				}
			});

			return alterou ? proximos : anteriores;
		});
	}, [
		agrupamentoAtivo,
		linhasRenderizadas,
		normalizarTexto,
		preservarAlturaNomeSetor,
	]);

	const legendaAgrupamento = useMemo(() => {
		if (!agruparPorEspecialidade) return [];

		const mapa = new Map();

		linhasRenderizadas.forEach((linha) => {
			if (!linha.__temAgrupamentoReal) return;

			const agrupamentoNome =
				linha.agrupamentoNome ||
				(agrupamentoAtivo === 'vinculo'
					? 'Sem vínculo'
					: 'Não informada');
			const agrupamentoKey = normalizarTexto(agrupamentoNome);

			if (!mapa.has(agrupamentoKey)) {
				mapa.set(agrupamentoKey, {
					key: agrupamentoKey,
					nome: agrupamentoNome,
					fundo: linha.especialidadeFundo || '#FFFFFF',
					texto: linha.especialidadeTextoCor || '#354052',
					classes: linha.especialidadeClasses,
					Icone: linha.IconeEspecialidade || FaStethoscope,
					turnos: [],
				});
			}

			mapa.get(agrupamentoKey).turnos.push({
				linhaKey: linha.linhaKey,
				turnoKey: linha.turnoKey,
				turnoNome: linha.turnoNome || 'Turno',
			});
		});

		return Array.from(mapa.values());
	}, [
		agruparPorEspecialidade,
		agrupamentoAtivo,
		linhasRenderizadas,
		normalizarTexto,
	]);

	const deveExibirTitulosAgrupamento =
		agruparPorEspecialidade &&
		linhasRenderizadas.some((linha) => linha.__temAgrupamentoReal);
	const quantidadeLinhasTituloAgrupamento = deveExibirTitulosAgrupamento
		? linhasRenderizadas.filter((linha) => linha.__temAgrupamentoReal).length
		: 0;
	const totalLinhasCorpoTabela =
		linhasRenderizadas.length + quantidadeLinhasTituloAgrupamento;

	const turnoSpanPorLinha = useMemo(() => {
		const spans = {};
		let index = 0;

		while (index < linhasRenderizadas.length) {
			const turnoAtual = linhasRenderizadas[index].turnoKey;
			let fim = index;

			while (
				fim < linhasRenderizadas.length &&
				linhasRenderizadas[fim].turnoKey === turnoAtual
			) {
				fim += 1;
			}

			const quantidadeTitulosAgrupamento = deveExibirTitulosAgrupamento
				? linhasRenderizadas
						.slice(index, fim)
						.filter((linha) => linha.__temAgrupamentoReal).length
				: 0;
			const rowSpan = fim - index + quantidadeTitulosAgrupamento;
			spans[linhasRenderizadas[index].linhaKey] = { show: true, rowSpan };

			for (let i = index + 1; i < fim; i += 1) {
				spans[linhasRenderizadas[i].linhaKey] = {
					show: false,
					rowSpan: 0,
				};
			}

			index = fim;
		}

		return spans;
	}, [deveExibirTitulosAgrupamento, linhasRenderizadas]);

	const alturaLinhaEscala = 188;
	const alturaMinimaCorpoEscala = useMemo(() => {
		const quantidadeTurnos = new Set(
			linhasRenderizadas.map((linha) => getPeriodoTurno(linha)),
		).size;
		const quantidadeLinhasMinima = deveGarantirTurnosBasicos
			? Math.max(3, linhasRenderizadas.length, quantidadeTurnos)
			: Math.max(2, linhasRenderizadas.length);

		if (!preservarAlturaNomeSetor) {
			return quantidadeLinhasMinima * alturaLinhaEscala;
		}

		// O rótulo do setor é vertical e não quebra linha. Mesmo com todos os
		// agrupamentos recolhidos, a grade precisa comportá-lo por completo.
		const caracteresNomeSetor = Array.from(
			String(nomeSetorEscala || 'SETOR').toUpperCase(),
		).length;
		// Em escrita vertical cada caractere não ocupa a altura total da fonte.
		// A medida real, capturada abaixo, tem prioridade; a estimativa apenas
		// evita uma primeira renderização curta antes dessa medição.
		const alturaNomeSetorEstimado = caracteresNomeSetor * 9 + 16;
		const alturaNomeSetor = Math.max(
			96,
			alturaNomeSetorEstimado,
			alturaNomeSetorRenderizado + 16,
		);

		return alturaNomeSetor;
	}, [
		deveGarantirTurnosBasicos,
		getPeriodoTurno,
		alturaNomeSetorRenderizado,
		linhasRenderizadas,
		nomeSetorEscala,
		preservarAlturaNomeSetor,
	]);
	const alturaMinimaLinhaEscala = Math.ceil(
		alturaMinimaCorpoEscala / Math.max(linhasRenderizadas.length, 1),
	);
	const quantidadeTurnosRenderizados = Math.max(
		1,
		new Set(linhasRenderizadas.map((linha) => linha.turnoKey)).size,
	);
	const alturaMinimaPorTurno = Math.ceil(
		alturaMinimaCorpoEscala / quantidadeTurnosRenderizados,
	);
	const quantidadeTitulosPorTurno = useMemo(() => {
		const quantidadePorTurno = new Map();

		linhasRenderizadas.forEach((linha) => {
			if (!linha.__temAgrupamentoReal) return;
			quantidadePorTurno.set(
				linha.turnoKey,
				(quantidadePorTurno.get(linha.turnoKey) || 0) + 1,
			);
		});

		return quantidadePorTurno;
	}, [linhasRenderizadas]);

	useEffect(() => {
		if (!preservarAlturaNomeSetor || typeof window === 'undefined') {
			return undefined;
		}

		const animationFrameId = window.requestAnimationFrame(() => {
			const alturaNomeSetor = Array.from(floatingLabelRefs.current).reduce(
				(maiorAltura, label) =>
					label?.dataset?.setorLabel === 'true'
						? Math.max(
							maiorAltura,
							label.scrollHeight || label.offsetHeight || 0,
						)
						: maiorAltura,
				0,
			);

			setAlturaNomeSetorRenderizado((alturaAnterior) =>
				alturaAnterior === alturaNomeSetor
					? alturaAnterior
					: alturaNomeSetor,
			);
		});

		return () => window.cancelAnimationFrame(animationFrameId);
	}, [linhasRenderizadas, nomeSetorEscala, preservarAlturaNomeSetor]);
	const larguraMinimaTabelaEscala = `${40 + 38 + colunasSemana.length * 206}px`;
	const rotuloOrdenacaoSubcategoria =
		agrupamentoAtivo === 'vinculo' ? 'Área de atuação' : 'Vínculo';
	const getRotuloTurno = (linha) => {
		const nome = String(linha?.turnoNome || 'Turno').toUpperCase();
		return abreviarTurnoCompacto && turnosCompactos[linha?.linhaKey]
			? nome.charAt(0)
			: nome;
	};

	const deveMostrarObservacao = (item) => {
		const tipo = getTipoKeyByItem(item);
		return (
			tipo === 'atestado' ||
			tipo === 'folga' ||
			tipo === 'filga' ||
			tipo === 'falta' ||
			tipo === 'licenca' ||
			tipo === 'licença'
		);
	};

	const deveMostrarEspecialidadeEVinculo = (item) => {
		const tipo = getTipoVisualKeyByItem(item);
		return (
			tipo === 'alocacao' ||
			tipo === 'ferias' ||
			tipo === 'vacancia'
		);
	};

	const getConteudoSecundarioItem = (item, tipoItem, tipoLabel) => {
		if (tipoItem === 'vacancia') return '';

		if (
			tipoItem === 'ferias' ||
			tipoItem === 'atestado' ||
			tipoItem === 'folga' ||
			tipoItem === 'filga' ||
			tipoItem === 'falta' ||
			tipoItem === 'licenca' ||
			tipoItem === 'licença'
		) {
			return tipoLabel;
		}

		const horarioPlantao24h = item?.__horarioPlantao24hRenderizacao || null;

		if (horarioPlantao24h?.hora_inicio || horarioPlantao24h?.hora_fim) {
			return `${formatarHora(horarioPlantao24h?.hora_inicio)} às ${formatarHora(horarioPlantao24h?.hora_fim)}`;
		}

		const horarioItem = getHorarioItem(item);
		const faixaHorariaTexto =
			horarioItem?.faixa_horaria_texto ||
			horarioItem?.nome_faixa_horaria ||
			item?.resumo?.faixa_horaria_texto ||
			item?.resumo?.nome_faixa_horaria ||
			'';

		if (faixaHorariaTexto) return faixaHorariaTexto;

		const horaInicio = horarioItem?.hora_inicio || item?.hora_inicio;
		const horaFim = horarioItem?.hora_fim || item?.hora_fim;

		if (horaInicio || horaFim) {
			return `${formatarHora(horaInicio)} às ${formatarHora(horaFim)}`;
		}

		if (tipoItem === 'alocacao') {
			return `${formatarHora(item.hora_inicio)} às ${formatarHora(item.hora_fim)}`;
		}

		return tipoLabel;
	};

	const getItemSortTitle = (item = {}) =>
		normalizarTexto(
			item?.resumo?.titulo ||
				item?.profissional?.nome ||
				item?.nome_profissional ||
				item?.nome ||
				'',
		);

	const getItemSortTime = (item = {}) => {
		const horarioPlantao24h = item?.__horarioPlantao24hRenderizacao || null;
		const horarioItem = getHorarioItem(item) || {};
		const hora =
			horarioPlantao24h?.hora_inicio ||
			horarioItem?.hora_inicio ||
			item?.hora_inicio ||
			'99:99';

		return String(hora || '99:99').slice(0, 5);
	};

	const compareText = (a = '', b = '') =>
		String(a || '').localeCompare(String(b || ''), 'pt-BR', {
			sensitivity: 'base',
		});

	const ordenarItensGrade = (items = [], linha = null) => {
		const getSubcategoria = (item = {}) =>
			normalizarTexto(
				agrupamentoAtivo === 'vinculo'
					? getEspecialidadeItem(item, item?.__linhaOrigem || linha)
					: getVinculoItem(item, item?.__linhaOrigem || linha),
			);

		return [...items].sort(({ item: itemA = {} }, { item: itemB = {} }) => {
			const tituloA = getItemSortTitle(itemA);
			const tituloB = getItemSortTitle(itemB);
			const horaA = getItemSortTime(itemA);
			const horaB = getItemSortTime(itemB);

			if (ordenacaoAtiva === 'alfabetica') {
				return (
					compareText(tituloA, tituloB) ||
					compareText(horaA, horaB)
				);
			}

			if (ordenacaoAtiva === 'subcategoria') {
				return (
					compareText(getSubcategoria(itemA), getSubcategoria(itemB)) ||
					compareText(tituloA, tituloB) ||
					compareText(horaA, horaB)
				);
			}

			return compareText(horaA, horaB) || compareText(tituloA, tituloB);
		});
	};

	const handleExportPdf = async () => {
		if (isExportingPdf) return;
		if (
			!Array.isArray(visualizacaoSetores) ||
			visualizacaoSetores.length === 0
		) {
			return;
		}

		setIsExportingPdf(true);
		setExportPdfError('');

		try {
			const exportarPdf = exportarPdfCustom || exportarPdfGridEscala;
			await exportarPdf({
				visualizacaoSetores,
				data,
				itensPorId,
				getTipoKeyByItem,
				semanaSelecionadaIndex,
				agruparPorEspecialidade,
				regime,
			});
		} catch (error) {
			console.error('Erro ao exportar PDF da escala:', error);
			setExportPdfError(
				'Não foi possível gerar o PDF. Tente novamente em instantes.',
			);
		} finally {
			setIsExportingPdf(false);
		}
	};


	if (escalaNaoEncontrada) {
		return (
			<div className='mx-auto mb-10 mt-0 max-w-[1600px] w-[90%] sm:w-[95%]'>
				<div className='flex flex-col items-center justify-center gap-4 rounded-[12px] border border-[#dde2e8] bg-white py-16 px-8 shadow-sm text-center'>
					<div className='flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF2F7] text-[#9BAFC5]'>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							width='32'
							height='32'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='1.5'
							strokeLinecap='round'
							strokeLinejoin='round'>
							<rect
								x='3'
								y='4'
								width='18'
								height='18'
								rx='2'
								ry='2'
							/>
							<line
								x1='16'
								y1='2'
								x2='16'
								y2='6'
							/>
							<line
								x1='8'
								y1='2'
								x2='8'
								y2='6'
							/>
							<line
								x1='3'
								y1='10'
								x2='21'
								y2='10'
							/>
						</svg>
					</div>
					<div>
						<p className='text-[18px] font-semibold text-[#17395f] mb-1'>
							Escala não encontrada
						</p>
						<p className='text-[14px] text-[#6B7280] max-w-[360px]'>
							Nenhuma escala foi encontrada para os parâmetros
							selecionados. Verifique os filtros e tente
							novamente.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className='mx-auto mb-10 mt-6 sm:mt-0 items-center max-w-[1600px] w-[90%] sm:w-[95%]'>
				<div className=''>
					<div className='mb-3 flex flex-col gap-3 '>
						{/* <p className='text-[#13335A] text-3xl font-semibold'>
						{data.escala?.nome_estabelecimento ||
							'Sem estabelecimento'}
					</p>{' '} */}

						<div className='flex flex-col sm:flex-row justify-between sm:items-end gap-5 sm:gap-3'>
							<p className='text-[#757575] text-sm text-center sm:text-left'>
								Última atualização:{' '}
								{ultimaAtualizacaoEscala
									? new Date(
											ultimaAtualizacaoEscala,
										).toLocaleDateString('pt-BR', {
											day: '2-digit',
											month: '2-digit',
											year: 'numeric',
										})
									: '--'}
							</p>

							<div className='flex flex-row flex-wrap justify-center sm:justify-end sm:flex-row sm:items-center gap-2 sm:ml-auto w-full sm:w-auto'>

								<button
									type='button'
									onClick={handleExportPdf}
									disabled={
										isExportingPdf ||
										visualizacaoSetores.length === 0
									}
									className='shrink-0 w-[190px] sm:w-auto justify-center max-h-[30px] sm:max-h-[35px] px-2 py-1.5 sm:px-[8px] sm:py-[8px] font-inter rounded-lg border border-[#B74644] bg-white text-[#B74644] hover:text-white hover:bg-[#B74644] transition-all duration-200 flex items-center text-[12px] sm:text-[15px] gap-1.5 sm:gap-2 disabled:opacity-60 disabled:cursor-not-allowed'>
									<FaRegFilePdf className='w-[12px] sm:w-[15px] h-auto' />
									{isExportingPdf ? 'Exportando...' : 'Exportar'}
								</button>
								{exportPdfError && (
									<div className='w-full text-center text-[12px] font-medium text-[#B74644] sm:w-auto sm:max-w-[280px] sm:text-right'>
										{exportPdfError}
									</div>
								)}
							</div>

							{/* 	<p className='text-[#757575] text-sm'>
										CNES: {data.escala?.cnes_estabelecimento || '--'} |
										Serviço: {data.escala?.nome_servico || '--'} | Regime:{' '}
										{data.escala?.regime || '--'}
									</p> */}
						</div>
						{/*   */}
						{/* <div className='flex flex-col items-center'>
							<div className='flex flex-wrap gap-3 sm:gap-5 text-[#4F5257] font-medium text-[15px] sm:text-[18px] justify-center  px-3'>
								<span>{tipoSetorSelecionado}</span>
								<span>{setorResumo}</span>
								<span className='capitalize'>
									{mesAnoExibicao}
								</span>
							</div>
						</div> */}
					</div>

					<div>
						<div className='w-full'>
							<div className='relative ml-[42px] mr-[41px] overflow-visible'>
								<div className='border border-[#D8DEE8] border-b-0 bg-[#FCFCFC] px-3 py-[8px] rounded-t-[20px]'>
									<div className='flex min-h-[32px] items-center justify-between gap-3'>
										<div className='text-[#4F5257] font-medium text-[15px] sm:text-[18px] text-start '>
											{capacidadeLabel}
										</div>

										{agruparPorEspecialidade && (
											<div className='relative z-[25] flex flex-1 items-center justify-end gap-4 overflow-visible'>
												<div className='flex items-center gap-1 rounded-full border border-[#D8DEE8] bg-white px-1 py-0.5'>
													{[
														{
															key: 'alfabetica',
															label: 'A-Z',
														},
														{
															key: 'horario',
															label: 'Horário',
														},
														{
															key: 'subcategoria',
															label: rotuloOrdenacaoSubcategoria,
														},
													].map((option) => (
														<button
															key={option.key}
															type='button'
															onClick={() =>
																setOrdenacaoAtiva(
																	option.key,
																)
															}
															className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition ${
																ordenacaoAtiva ===
																option.key
																	? 'bg-[#0B4D7A] text-white'
																	: 'text-[#526173] hover:bg-[#EEF2F7] hover:text-[#0B4D7A]'
															}`}>
															{option.label}
														</button>
													))}
												</div>

												{[
													// 'vinculo',
													'especialidade',
												].map((tipo) => {
													const ativo =
														agrupamentoAtivo ===
														tipo;
													const aberto =
														ativo &&
														legendaAgrupamentoAberta ===
															tipo;

													return (
														<div
															key={tipo}
															className='relative'>
															<button
																type='button'
																onClick={() =>
																	selecionarAgrupamento(
																		tipo,
																	)
																}
																aria-expanded={
																	aberto
																}
																className={`flex items-center gap-0.5 bg-transparent px-1 py-1 text-[13px] font-medium transition-colors hover:text-[#0B4D7A] ${
																	ativo
																		? 'text-[#0B4D7A]'
																		: 'text-[#65686D]'
																}`}>
																<span className='capitalize'>
																	{tipo ===
																	'vinculo'
																		? 'Vínculo'
																: rotuloAgrupamento}
																</span>
																<MdKeyboardArrowDown
																	className={`h-5 w-5 transition-transform ${
																		aberto
																			? 'rotate-180'
																			: ''
																	}`}
																/>
															</button>

															{aberto && (
																<div className='absolute right-0 top-[calc(100%+7px)] z-[25] w-[260px] rounded-md border border-[#D8DEE8] bg-white p-2 shadow-lg'>
																	{legendaAgrupamento.length >
																	0 ? (
																		<div className='flex flex-col items-stretch gap-1 text-left'>
																			{legendaAgrupamento.map(
																				(
																					item,
																				) => {
																					const temMaisDeUmTurno =
																						item
																							.turnos
																							.length >
																						1;

																					return (
																						<div
																							key={
																								item.key
																							}
																							className='relative w-full'>
																							<button
																								type='button'
																								onClick={(
																									event,
																								) => {
																									if (
																										!temMaisDeUmTurno
																									) {
																										rolarParaLinha(
																											item
																												.turnos[0]
																												?.linhaKey,
																										);
																										return;
																									}

																									alternarMenuTurnos(
																										item,
																										event.currentTarget,
																									);
																								}}
																								className='flex w-full items-center justify-start gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-[#EEF2F7]'
																								title={
																									temMaisDeUmTurno
																										? 'Escolher turno'
																: `Ir para ${tipo === 'vinculo' ? 'vínculo' : 'área de atuação'}`
																								}>
																								<span
																									className={`h-3 w-3 shrink-0 rounded-full border ${item.classes?.legendDot || 'bg-[#2F8F2F]/45'} ${item.classes?.border || 'border-[#2F8F2F]'}`}
																								/>
																								<span className='text-[11px] font-medium uppercase text-[#4F5257]'>
																									{
																										item.nome
																									}
																								</span>
																							</button>
																						</div>
																					);
																				},
																			)}
																		</div>
																	) : (
																		<p className='px-2 py-1 text-left text-[12px] text-[#757575]'>
																			Nenhuma
																			legenda
																			disponível.
																		</p>
																	)}
																</div>
															)}
														</div>
													);
												})}
											</div>
										)}
									</div>
								</div>

								<div
									className='absolute left-full top-1/2 z-20 ml-1 -translate-y-1/2'
									onMouseEnter={() =>
										setMostrarLegendaCards(true)
									}
									onMouseLeave={() =>
										setMostrarLegendaCards(false)
									}>
									<button
										type='button'
										onClick={() =>
											setMostrarLegendaCards(
												(prev) => !prev,
											)
										}
										aria-label='Mostrar legenda dos cards'
										className='flex h-8 w-8 items-center justify-center text-[#85888d] transition-colors hover:text-[#0B4D7A]'>
										<FiInfo size={25} />
									</button>

									{mostrarLegendaCards && (
										<div className='absolute border border-gray-200 right-0 top-[calc(100%+8px)] z-20 min-w-[150px] rounded-[5px] bg-white p-3 shadow-lg'>
											{/* <p className='mb-2 text-center text-[13px] font-semibold  text-[#4F5257]'>
												Legenda dos cards
											</p> */}
											<div className='flex flex-col items-center'>
												{legendaCards.map(
													(item, index) => (
														<React.Fragment
															key={item.label}>
															<div
																className={`w-full text-center text-[14px] font-medium ${item.className}`}>
																{item.label.toUpperCase()}
															</div>
															{index <
																legendaCards.length -
																	1 && (
																<div className='my-2 h-[1px] w-[90%] bg-[#E5E7EB]' />
															)}
														</React.Fragment>
													),
												)}
											</div>
										</div>
									)}
								</div>
							</div>

							<div
								ref={gradeScrollRef}
								onScroll={atualizarTitulosAgrupamento}
								className='relative w-full overflow-x-auto overflow-y-visible overscroll-x-contain rounded-b-md touch-auto [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar-track]:bg-[#F8FAFC] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#CBD5E1]'>
								<table
									className='w-full table-fixed'
									style={{
										minWidth: larguraMinimaTabelaEscala,
									}}>
									<thead>
										<tr className=''>
											<th className='sticky left-0 top-0 z-[15] w-[40px] border-r border-[#d5d5d5] bg-white '>
												{!modoMensal && (
												<div className='w-full flex justify-center'>
													<button
														type='button'
														onClick={() =>
															setSemanaSelecionadaIndex(
																(prev) =>
																	Math.max(
																		prev -
																			1,
																		0,
																	),
															)
														}
														disabled={
															semanaSelecionadaIndex <=
															0
														}
														className='h-7 w-7 rounded-full flex items-center content-center justify-center text-[#4F5257] disabled:opacity-40 rotate-280 disabled:cursor-not-allowed hover:text-[#004A80] hover:bg-[#f4f4f4]'>
														<MdKeyboardArrowLeft className='w-7 h-7' />
													</button>
												</div>
												)}
											</th>

											{colunasSemana.map((colunaDia) => (
												<th
													key={`${colunaDia.nome_dia_semana}-${colunaDia.data || 'sem-data'}`}
													className='sticky top-0 z-10 w-[206px] bg-[#0B4D7A] text-white text-[18px] font-semibold text-center py-1.5 border-r border-[#d5d5d5] last:border-r-0'>
													<div className='leading-tight'>
														<div>
															{
																colunaDia.nome_dia_semana
															}
														</div>
														<div className='text-[12px] font-normal'>
															{colunaDia.data
																? formatarDataPtBr(
																		colunaDia.data,
																	)
																: '--'}
														</div>
													</div>
												</th>
											))}
											<th className='sticky right-0 top-0 z-[15] w-[38px] min-w-[38px] bg-white border-[#d5d5d5]'>
												{!modoMensal && (
												<div className='w-full flex justify-center'>
													<button
														type='button'
														onClick={() =>
															setSemanaSelecionadaIndex(
																(prev) =>
																	Math.min(
																		prev +
																			1,
																		Math.max(
																			semanasDoMes.length -
																				1,
																			0,
																		),
																	),
															)
														}
														disabled={
															semanaSelecionadaIndex >=
															semanasDoMes.length -
																1
														}
														className='h-7 w-7 rounded-full flex items-center content-center justify-center text-[#4F5257] disabled:opacity-40 rotate-180 disabled:cursor-not-allowed hover:text-[#004A80] hover:bg-[#f4f4f4]'>
														<MdKeyboardArrowLeft className='w-7 h-7' />
													</button>
												</div>
												)}
											</th>
										</tr>
									</thead>
									<tbody className='rounded-md'>
										{linhasRenderizadas.map((linha, rowIndex) => {
											const ultimaLinhaDoTurno =
												rowIndex === linhasRenderizadas.length - 1 ||
												linhasRenderizadas[rowIndex + 1]?.turnoKey !==
													linha.turnoKey;
											const mostrarTituloAgrupamento =
												deveExibirTitulosAgrupamento &&
												linha.__temAgrupamentoReal;
											const agrupamentoNomeLinha =
												linha.agrupamentoNome ||
												(agrupamentoAtivo === 'vinculo'
													? 'Sem vínculo'
													: 'Não informada');
											const agrupamentoColapsoKey = `${linha.turnoKey}-${normalizarTexto(
												agrupamentoNomeLinha,
											)}`;
															const agrupamentoColapsado =
																Boolean(
																	agrupamentosColapsados[
																		agrupamentoColapsoKey
																	],
																);
															const agrupamentoCor =
												linha.especialidadeTextoCor ||
												'#0B4D7A';
											const quantidadeCardsPorColuna =
												colunasSemana.map(
													(colunaDia) => {
														const key = `${colunaDia.data || colunaDia.nome_dia_semana}-${linha.linhaKey}`;
														return (
															celulasPorDiaELinha[
																key
															] || []
														).length;
													},
												);
															const slotsPorColuna = Math.max(
																1,
																...quantidadeCardsPorColuna,
															);
											const possuiCardsNaLinha =
												quantidadeCardsPorColuna.some(
													(quantidadeCards) => quantidadeCards > 0,
												);
											const exibirFantasmasDoTurno =
												preservarAlturaNomeSetor &&
												!mostrarTituloAgrupamento &&
												!possuiCardsNaLinha;
											const alturaConteudoLinha =
																possuiCardsNaLinha
																	? preservarAlturaNomeSetor
																		? slotsPorColuna * 165 +
																				Math.max(
																					0,
																					slotsPorColuna - 1,
																				) *
																					8
																		: Math.max(
																			alturaMinimaLinhaEscala,
																			slotsPorColuna * 165 +
																				Math.max(
																					0,
																					slotsPorColuna - 1,
																				) *
																					8,
																)
															: 0;
											// A reserva mínima fica na última linha do turno. Desse
											// modo as abas começam juntas no topo e só são afastadas
											// quando uma delas realmente exibe seus cards.
											const alturaReservaTurno =
												preservarAlturaNomeSetor && ultimaLinhaDoTurno
													? Math.max(
														0,
														alturaMinimaPorTurno -
															(quantidadeTitulosPorTurno.get(
																linha.turnoKey,
															) ||
																0) *
																34,
													)
													: 0;
											const alturaListaAberta = Math.max(
												alturaConteudoLinha + 12,
												alturaReservaTurno,
											);
											const turnoSpan =
												turnoSpanPorLinha[
													linha.linhaKey
												];

											return (
												<React.Fragment
													key={linha.linhaKey}>
													{mostrarTituloAgrupamento && (
																		<tr
																			key={`${linha.linhaKey}-titulo`}
																			className={
																				preservarAlturaNomeSetor ? 'h-0' : undefined
																			}>
															{rowIndex === 0 && (
																<td
																	rowSpan={
																		totalLinhasCorpoTabela
																	}
																	className='sticky left-0 z-[5] w-[40px] min-w-[40px] max-w-[40px] overflow-hidden bg-[#0B4D7A] border-r border-[#d5d5d5] rounded-tl-md align-top relative'
																>
																	<div
																		ref={
																			registerFloatingLabel
																		}
																		data-setor-label='true'
																		className='absolute left-1/2 top-0 text-white text-[18px] font-semibold [writing-mode:vertical-rl] tracking-wide whitespace-nowrap'
																		style={{
																			transform:
																				'translateX(-50%) translateY(var(--floating-label-y, 24px)) rotate(180deg)',
																		}}>
																		{linhasRenderizadas[0]
																			?.nomeSetorEscala
																			? linhasRenderizadas[0].nomeSetorEscala.toUpperCase()
																			: 'SETOR'}
																	</div>
																</td>
															)}
															<td
																colSpan={
																	colunasSemana.length
																}
																className={`${preservarAlturaNomeSetor ? getLinhaTurnoBackgroundClass(linha) : 'bg-white'} px-1.5 py-1 align-top`}>
																<button
																	type='button'
																	onClick={() =>
																		alternarAgrupamentoColapsado(
																			agrupamentoColapsoKey,
																		)
																	}
																	className='flex w-full items-center justify-between rounded-[7px] px-3 py-1.5 text-left shadow-sm transition brightness-100 hover:brightness-95'
																	style={{
																		backgroundColor:
																			agrupamentoCor,
																	}}>
																	<span className='flex min-w-0 items-center gap-2'>
																					<span
																						ref={registerTituloAgrupamento}
																						className={`inline-block truncate text-[13px] font-bold uppercase tracking-[0.08em] text-white ${seguirTituloAgrupamentoNoScroll ? 'relative z-[1]' : ''}`}>
																			{
																				agrupamentoNomeLinha
																			}
																		</span>
																	</span>
																	<MdKeyboardArrowDown
																		className={`h-5 w-5 shrink-0 text-white transition-transform ${
																			agrupamentoColapsado
																				? ''
																				: 'rotate-180'
																		}`}
																	/>
																</button>
															</td>
															{turnoSpan?.show && (
																<td
																	rowSpan={
																		turnoSpan.rowSpan
																	}
																																			className={`sticky right-0 z-[5] w-[44px] min-w-[44px] max-w-[44px] overflow-hidden ${getTurnoBackgroundClass(linha)} align-top relative ${preservarAlturaNomeSetor && getPeriodoTurno(linha) === 'noite' ? 'border-t-2 border-white' : ''} ${rowIndex === 0 ? 'rounded-tr-md' : ''}`}
																>
																	<div
																				ref={
																					registerFloatingLabel
																				}
																				data-turno-key={linha.linhaKey}
																				data-turno-nome={linha.turnoNome}
																																			className='absolute left-1/2 top-0 py-2 text-[14px] sm:text-[18px] text-white font-semibold [writing-mode:vertical-rl] whitespace-nowrap'
																		style={{
																			transform:
																				'translateX(-50%) translateY(var(--floating-label-y, 24px)) rotate(180deg)',
																		}}>
																				{getRotuloTurno(linha)}
																	</div>
																</td>
															)}
														</tr>
													)}

															<tr
																key={`${linha.linhaKey}-cards`}
																className={
																	preservarAlturaNomeSetor ? 'h-0' : undefined
																}
																ref={(element) => {
															if (element) {
																linhasRefs.current[
																	linha.linhaKey
																] = element;
															}
														}}>
														{rowIndex === 0 &&
															!mostrarTituloAgrupamento && (
															<td
																rowSpan={
																	totalLinhasCorpoTabela
																}
																className='sticky left-0 z-[5] w-[40px] min-w-[40px] max-w-[40px] overflow-hidden bg-[#0B4D7A] border-r border-[#d5d5d5] rounded-tl-md align-top relative'
																>
																	<div
																		ref={
																			registerFloatingLabel
																		}
																		data-setor-label='true'
																			className='absolute left-1/2 top-0 text-white text-[18px] font-semibold [writing-mode:vertical-rl] tracking-wide whitespace-nowrap'
																	style={{
																		transform:
																			'translateX(-50%) translateY(var(--floating-label-y, 24px)) rotate(180deg)',
																	}}>
																			{linhasRenderizadas[0]
																			?.nomeSetorEscala
																			? linhasRenderizadas[0].nomeSetorEscala.toUpperCase()
																		: 'SETOR'}
																</div>
															</td>
														)}

												{colunasSemana.map(
													(colunaDia) => {
														const key = `${colunaDia.data || colunaDia.nome_dia_semana}-${linha.linhaKey}`;
														const itensSemanais =
															celulasPorDiaELinha[
																key
															] || [];
																		const itensSemanaisOrdenados =
																			ordenarItensGrade(
																				itensSemanais,
																				linha,
																			);
																		const quantidadeFantasmas =
																														mostrarTituloAgrupamento && possuiCardsNaLinha
																															? Math.max(
																																		0,
																																		slotsPorColuna -
																																		itensSemanaisOrdenados.length,
																																	)
																															: exibirFantasmasDoTurno
																																? 1
																																: 0;
																		return (
															<td
																key={key}
																																		className={`w-[206px] align-top overflow-hidden ${possuiCardsNaLinha || exibirFantasmasDoTurno ? 'p-1.5' : 'p-0'} ${getLinhaTurnoBackgroundClass(linha)}`}>
																		<div
																			className='relative overflow-hidden transition-[height] duration-300 ease-in-out'
																			style={{
																				height:
																																	!possuiCardsNaLinha || agrupamentoColapsado
																																		? `${Math.max(
																																			alturaReservaTurno,
																																			exibirFantasmasDoTurno ? 177 : 0,
																																		)}px`
																				: `${alturaListaAberta}px`,
																			}}>
																			<div
																				className='absolute left-0 top-0 flex w-full flex-col gap-2 transition-transform duration-300 ease-in-out'
																					style={{
																						transform:
																																	agrupamentoColapsado ||
																																				(!possuiCardsNaLinha &&
																																					!exibirFantasmasDoTurno)
																								? `translateY(-${alturaConteudoLinha + 12}px)`
																								: 'translateY(0)'
																					}}>
																	{itensSemanaisOrdenados.length >
																		0 &&
																		itensSemanaisOrdenados.map(
																			(
																				{
																					item,
																					celula,
																					datasRepresentadas,
																				},
																				itemIndex,
																			) => {
																				const linhaDados =
																					item?.__linhaOrigem ||
																					linha;
																				const estiloItem =
																					getStyleByItem(
																						item,
																					);
																				const tipoLabel =
																					getTipoLabelByItem(
																						item,
																					);
																				const tipoItem =
																					getTipoKeyByItem(
																						item,
																					);
																				const tipoItemVisual =
																					getTipoVisualKeyByItem(
																						item,
																					);
																				const mostrarHorario =
																					tipoItemVisual ===
																					'alocacao';
																				const mostrarObservacao =
																					deveMostrarObservacao(
																						item,
																					);
																				const mostrarEspecialidadeEVinculo =
																					deveMostrarEspecialidadeEVinculo(
																						item,
																					);
																				const especialidadeItemLabel =
																					getEspecialidadeItem(
																						item,
																						linhaDados,
																					);
																				const especialidadeClasses =
																					linhaDados?.especialidadeClasses ||
																					linha.especialidadeClasses ||
																					getEspecialidadeClasses();
																				const vinculoItemLabel =
																					getVinculoItem(
																						item,
																						linhaDados,
																					);
																				const vinculoClasses =
																					getVinculoClasses(
																						vinculoItemLabel ||
																							'Sem vínculo',
																					);
																				const hasVinculoItemLabel =
																					Boolean(
																						String(
																							vinculoItemLabel ||
																								'',
																						).trim(),
																					);
																				const agrupandoPorVinculo =
																					agruparPorEspecialidade &&
																					agrupamentoAtivo ===
																						'vinculo';
																				const conteudoSecundario =
																					getConteudoSecundarioItem(
																						item,
																						tipoItemVisual,
																						tipoLabel,
																					);
																		const isCardViewOnly =
																			isViewOnlyMode ||
																			tipoItem ===
																				'troca';
																		const tituloCompletoItem = (
																			item?.resumo?.titulo || 'VACÂNCIA'
																		).toUpperCase();
																		const tituloExibidoItem =
																			compactarNomeProfissionalNoCard &&
																			tipoItemVisual !== 'vacancia'
																				? compactarTituloProfissionalNoCard(
																						tituloCompletoItem,
																					)
																				: smartTruncateName(tituloCompletoItem);
																		const podeEditar =
																					canManageItems &&
																					(isCardViewOnly ||
																						item
																							?.acoes
																							?.pode_editar !==
																							false);
																				return (
																					<div
																						key={`${item?.id || 'item'}-${itemIndex}`}
																						data-scale-card
																						className='relative cursor-default overflow-hidden rounded-[9px] bg-white flex flex-col gap-[6px] border h-[165px] w-[99%] mx-auto pl-[17px] pr-[12px] py-[10px] shadow border-[#dfdfdf]'>
																						{mostrarEspecialidadeEVinculo && (
																							<span
																								className={`absolute left-0 top-0 h-full w-[6px] ${especialidadeClasses.dot}`}
																							/>
																						)}
																						<div
																			title={tituloCompletoItem}
																							className={`w-full text-[13px] font-bold rounded-[4px] truncate text-center ${tipoItem === 'vacancia' ? 'min-h-[30px] flex items-center justify-center px-2 py-2' : 'h-[30px] min-h-[30px] max-h-[30px] flex items-center justify-center px-2'} ${getBadgeClassByItem(item)}`}>
																			{tituloExibidoItem}
																						</div>
																						{item.tipo_item !==
																							'VACANCIA' &&
																							''}

																						<div
																							className={`text-[12px] font-inter text-center ${mostrarHorario ? 'font-normal text-[#B8B8B8]' : conteudoSecundario === '' ? 'hidden' : `font-semibold ${estiloItem.typeTextClass}`}`}>
																							{
																								conteudoSecundario
																							}
																						</div>
																						{mostrarEspecialidadeEVinculo && (
																							<>
																								<div
																									className={`flex h-[25px] min-h-[25px] max-h-[25px] w-full min-w-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border text-[12px] text-center px-2 whitespace-nowrap ${tipoItemVisual === 'vacancia' ? 'mt-auto' : ''} ${
																										agrupandoPorVinculo
																											? `font-semibold uppercase ${especialidadeClasses.text} ${especialidadeClasses.border} ${especialidadeClasses.background}`
																											: 'border-[#ececec] bg-[#f8f8f8] text-[#65686d]'
																									}`}>
																									{agrupandoPorVinculo && (
																										<span
																											className={`h-1.5 w-1.5 shrink-0 rounded-full ${especialidadeClasses.dot}`}
																										/>
																									)}
																									<span className='block min-w-0 max-w-full truncate overflow-hidden whitespace-nowrap'>
																										{agrupandoPorVinculo
																											? vinculoItemLabel ||
																												'Sem vínculo'
																											: especialidadeItemLabel ||
																												'-'}
																									</span>
																								</div>
																								<div
																									className={`flex h-[25px] min-h-[25px] max-h-[25px] w-full min-w-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border px-1 text-center text-[12px] whitespace-nowrap ${
																										agrupandoPorVinculo
																											? 'border-[#ececec] bg-[#f8f8f8] text-[#65686d]'
																											: hasVinculoItemLabel
																												? `font-semibold uppercase ${vinculoClasses.text} ${vinculoClasses.border} ${vinculoClasses.background}`
																												: 'border-[#ececec] bg-[#f8f8f8] text-[#65686d]'
																									}`}>
																									{!agrupandoPorVinculo &&
																										hasVinculoItemLabel && (
																										<span
																											className={`h-1.5 w-1.5 shrink-0 rounded-full ${vinculoClasses.dot}`}
																										/>
																									)}
																									<span className='block min-w-0 max-w-full truncate overflow-hidden whitespace-nowrap'>
																										{agrupandoPorVinculo
																											? especialidadeItemLabel ||
																												'-'
																											: vinculoItemLabel ||
																												'-'}
																									</span>
																								</div>
																							</>
																						)}
																						{mostrarObservacao && (
																							<div className='border border-[#ececec] rounded-[8px] text-[13px] text-[#65686d] text-center py-[4px] px-2 flex-1 min-h-0 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:w-[2px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#d1d5db]'>
																								{item
																									?.resumo
																									?.observacao &&
																								item?.resumo?.observacao.trim() !==
																									''
																									? item
																											?.resumo
																											?.observacao
																									: 'Sem observação'}
																							</div>
																						)}
																						{/* {!mostrarObservacao && (
																							<div className='flex-1' />
																						)} */}
																						<div className='flex justify-between gap-2 text-[12px] px-1'>
																							<button
																								onClick={() =>
																									podeEditar &&
																									(() => {
																										const dadosEdicao =
																											logDadosProfissionalPreenchimento(
																												item,
																												celula,
																												datasRepresentadas,
																												linhaDados,
																											);

																										onEditItem?.(
																											{
																												...item,
																												forceViewOnly:
																													isCardViewOnly,
																												celula,
																												datasRepresentadas,
																												dados_edicao:
																													dadosEdicao,
																											},
																										);
																									})()
																								}
																								disabled={
																									!podeEditar
																								}
																								className='text-[#2d6ca0] hover:underline disabled:opacity-40 disabled:cursor-not-allowed'>
																								{isCardViewOnly
																									? 'Ver'
																									: 'Ver'}
																							</button>
																							{/* <button
																								type='button'
																								onClick={() =>
																									abrirPopupExclusao(
																										item.id,
																									)
																								}
																								disabled={
																									!podeExcluir
																								}
																								className='text-[#d06e6e] hover:underline disabled:opacity-40 disabled:cursor-not-allowed'>
																								Excluir
																							</button> */}
																						</div>
																					</div>
																				);
																					},
																				)}
																			{quantidadeFantasmas >
																				0 &&
																				Array.from({
																					length: quantidadeFantasmas,
																				}).map((_, fantasmaIndex) => (
																					<div
																						key={`fantasma-${fantasmaIndex}`}
																						aria-hidden='true'
																						className='h-[165px] w-[99%] mx-auto rounded-[9px] border border-dashed border-[#D4DFEA] bg-white/35'
																					/>
																				))}
																			</div>
																		</div>
															</td>
														);
													},
												)}

												{turnoSpan?.show &&
													!mostrarTituloAgrupamento && (
													<td
														rowSpan={
															turnoSpan.rowSpan
														}
																																					className={`sticky right-0 z-[5] w-[44px] min-w-[44px] max-w-[44px] overflow-hidden ${getTurnoBackgroundClass(linha)} align-top relative ${preservarAlturaNomeSetor && getPeriodoTurno(linha) === 'noite' ? 'border-t-2 border-white' : ''} ${rowIndex === 0 ? 'rounded-tr-md' : ''}`}
																		>
														<div
																			ref={
																				registerFloatingLabel
																			}
																			data-turno-key={linha.linhaKey}
																			data-turno-nome={linha.turnoNome}
																																					className='absolute left-1/2 top-0 py-2 text-[14px] sm:text-[18px] text-white font-semibold [writing-mode:vertical-rl] whitespace-nowrap'
															style={{
																transform:
																	'translateX(-50%) translateY(var(--floating-label-y, 24px)) rotate(180deg)',
															}}>
																		{getRotuloTurno(linha)}
														</div>
																	</td>
																	)}
																</tr>
																</React.Fragment>
											);
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>


				</div>
			</div>

			{menuTurnosFlutuante &&
				typeof document !== 'undefined' &&
				createPortal(
					<div
						className='fixed z-20 min-w-[160px] max-h-[260px] overflow-y-auto rounded-md border border-[#D8DEE8] bg-white p-1.5 shadow-lg'
						style={{
							left: menuTurnosFlutuante.left,
							top: menuTurnosFlutuante.top,
						}}>
						{menuTurnosFlutuante.turnos.map((turno) => (
							<button
								type='button'
								key={turno.linhaKey}
								onClick={() => rolarParaLinha(turno.linhaKey)}
								className='block w-full rounded px-3 py-1.5 text-left text-[12px] font-medium uppercase text-[#4F5257] hover:bg-[#EEF2F7]'>
								{turno.turnoNome}
							</button>
						))}
					</div>,
					document.body,
				)}

			{itemIdParaExcluir && (
				<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4'>
					<div className='w-full max-w-[516px] rounded-lg border border-[#d9d9d9] bg-[#f5f5f5] shadow-xl'>
						<div className='px-8 py-7'>
							<div className='mb-5 flex items-center gap-3'>
								<div className='flex h-12 w-12 items-center justify-center rounded-full bg-[#e2e2e2] text-[#b03f3f]'>
									<FiAlertTriangle size={24} />
								</div>
								<h3 className='text-[20px] font-semibold font-inter text-[#4f5257]'>
									Confirmar Exclusão
								</h3>
							</div>

							<p className='mb-2 text-[16px] font-normal text-start text-[#595d63]'>
								Você tem certeza que deseja excluir o
								profissional da escala?
							</p>
							<p className='text-[16px] font-normal text-start text-[#595d63]'>
								Esta ação não pode ser desfeita.
							</p>

							<div className='mt-8 flex justify-center gap-4'>
								<button
									type='button'
									onClick={fecharPopupExclusao}
									disabled={isDeletingItem}
									className='min-w-[100px] rounded-lg border border-[#d7d7d7] bg-[#f6f6f6] px-5 py-2 text-[14px] font-medium text-[#60656d] hover:bg-[#ececec] disabled:cursor-not-allowed disabled:opacity-70'>
									Cancelar
								</button>
								<button
									type='button'
									onClick={confirmarExclusaoItem}
									disabled={isDeletingItem}
									className='min-w-[100px] rounded-lg border border-[#b14a46] bg-[#bc514d] px-5 py-2 text-[14px] font-semibold text-white hover:bg-[#aa4743] disabled:cursor-not-allowed disabled:opacity-70'>
									{isDeletingItem
										? 'Excluindo...'
										: 'Excluir'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

export default ApiScaleGrid;
